import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MentionDropdown from './MentionDropdown';
import { useStore } from 'reactflow';
import { X, Check, SendHorizontal, Play, Pause, Sparkles, Music, Sparkle, ChevronDown, Box, Pin, Maximize2, Minus, Copy, Heading1, Heading2, Heading3, Pilcrow, Indent, ListOrdered, Loader2, Wand2, ImageOff } from 'lucide-react';
import QuickTooltip from '../components/QuickTooltip';
import { DescriptionIcon, descBadgeStyle, descBadgeHover } from '../components/DescriptionBadge';
import InlineColorPicker from '../components/InlineColorPicker';
import VoiceMicButton from '../components/VoiceMicButton'; // 🎤 语音输入按钮
import { ToolBtn } from './NodeCommons';
import { useInputProtection } from '../hooks/useInputProtection';
import api, { getToken } from '../api'; // 🆕
import { API_BASE_URL } from '../constants';
import StoryboardEditor from './StoryboardEditor';
import { findPreset, findToolsetByPresetId, findSkillboxPreset } from '../utils/presetTree';
import { writeNodeDefaults } from '../utils/nodeDefaults';
import { copyTextToClipboard } from '../utils/browserCompat'; // http 站点没有 navigator.clipboard
import SlatePromptEditor from '../components/SlatePromptEditor'; // 🆕 Slate.js 富文本编辑器
import SlateStoryboardEditor from '../components/SlateStoryboardEditor'; // 🆕 分镜大师 Slate 编辑器（全屏编辑用）
import { Text } from 'slate'; // 🆕 Slate Text 类型判断
import { getCommandState } from '../utils/mentionUtils'; // 🆕 @ / # 命令检测
import { getSlateCommandState } from '../utils/slateCommandUtils'; // 🆕 Slate @ / 命令检测

const MODEL_CACHE_TTL_MS = 60 * 1000;
const modelListCache = new Map(); // mediaType -> { list, expiresAt }
const modelListInFlight = new Map(); // mediaType -> Promise<list>

// 🎨 分镜大师画面风格预设缓存（首次打开下拉框时拉取，60s TTL，跨节点共享）
const STYLE_PRESET_TTL_MS = 60 * 1000;
let stylePresetCache = null; // { list, expiresAt }
let stylePresetInFlight = null; // Promise<list>
function fetchStoryboardStylePresets() {
  const now = Date.now();
  if (stylePresetCache && stylePresetCache.expiresAt > now) return Promise.resolve(stylePresetCache.list);
  if (stylePresetInFlight) return stylePresetInFlight;
  stylePresetInFlight = api.get('/api/ai/storyboard-style-presets')
    .then(res => {
      const list = Array.isArray(res?.stylePresets) ? res.stylePresets : [];
      stylePresetCache = { list, expiresAt: Date.now() + STYLE_PRESET_TTL_MS };
      return list;
    })
    .catch(() => [])
    .finally(() => { stylePresetInFlight = null; });
  return stylePresetInFlight;
}

function withTimeout(promise, timeoutMs, label = '请求') {
  if (!timeoutMs || timeoutMs <= 0) return promise;
  let timer = null;
  const timeoutPromise = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error(`${label}超时（${timeoutMs}ms）`)), timeoutMs);
  });
  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timer) clearTimeout(timer);
  });
}

async function fetchModelsFromApi(mediaType) {
  let list = null;
  let firstError = null;

  try {
    list = await api.get(`/api/models/by-type/${mediaType}`);
    if (Array.isArray(list)) {
      list = list.filter(m => m && m.enabled !== 0);
    }
  } catch (e) {
    firstError = e;
  }

  // 首接口失败或返回空列表，都强制回退到 /api/models
  if (!list || list.length === 0) {
    try {
      const arr = await api.get('/api/models');
      if (Array.isArray(arr)) {
        list = arr.filter(m => m.model_type === mediaType && m.enabled !== 0);
      }
    } catch (fallbackError) {
      throw fallbackError || firstError || new Error('加载模型失败');
    }
  }

  if (!Array.isArray(list)) {
    throw firstError || new Error('模型数据格式无效');
  }

  return list;
}

async function fetchModelsWithRetry(mediaType, { timeoutMs = 15000, retries = 2 } = {}) {
  let lastErr = null;
  for (let i = 0; i <= retries; i++) {
    try {
      return await withTimeout(fetchModelsFromApi(mediaType), timeoutMs, '模型加载');
    } catch (e) {
      lastErr = e;
      if (i < retries) {
        const delay = 300 * (i + 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw lastErr || new Error('加载模型失败');
}

async function getModelsForType(mediaType, { force = false } = {}) {
  const now = Date.now();
  const cached = modelListCache.get(mediaType);
  if (!force && cached && cached.expiresAt > now) {
    return cached.list;
  }

  if (!force && modelListInFlight.has(mediaType)) {
    return modelListInFlight.get(mediaType);
  }

  const task = fetchModelsWithRetry(mediaType, { timeoutMs: 15000, retries: 2 })
    .then((list) => {
      modelListCache.set(mediaType, { list, expiresAt: Date.now() + MODEL_CACHE_TTL_MS });
      return list;
    })
    .finally(() => {
      modelListInFlight.delete(mediaType);
    });

  modelListInFlight.set(mediaType, task);
  return task;
}

// MJ Imagine / Blend 每次固定出 4 图，不允许修改批次数
function isMjBatchLockedModel(model) {
  if (!model) return false;
  try {
    const extra = typeof model.extra_json === 'string'
      ? JSON.parse(model.extra_json)
      : (model.extra_json || {});
    const mid = extra.model_id || '';
    if (mid === 'mj_imagine' || mid === 'mj_blend') return true;
  } catch (_) {}
  const name = String(model.name || '');
  return /MJ\s*生图|Imagine|MJ\s*混合|Blend/i.test(name);
}

function isImageBlenderModel(model) {
  if (!model) return false;
  if (String(model.model_type || '').toLowerCase() !== 'image') return false;

  const name = String(model.name || '');
  const type = String(model.type || '');
  const description = String(model.description || '');

  let extraText = '';
  try {
    const extra = typeof model.extra_json === 'string'
      ? JSON.parse(model.extra_json)
      : (model.extra_json || {});
    extraText = JSON.stringify(extra);
  } catch (_) {
    extraText = String(model.extra_json || '');
  }

  const merged = `${name} ${type} ${description} ${extraText}`;
  return /mg[_-]?blender/i.test(merged)
    || /image\s*blender/i.test(merged)
    || /电图融创|融创/.test(merged);
}

function isAspectRatioSuppressedModel(model) {
  if (!model) return false;

  const name = String(model.name || '');
  const type = String(model.type || '');
  const description = String(model.description || '');

  let extraText = '';
  try {
    const extra = typeof model.extra_json === 'string'
      ? JSON.parse(model.extra_json)
      : (model.extra_json || {});
    extraText = JSON.stringify(extra);
  } catch (_) {
    extraText = String(model.extra_json || '');
  }

  const merged = `${name} ${type} ${description} ${extraText}`;
  return /comfyui|liblib|万物细节|超分|upscale|detail|enhance|enhanced|修复|放大|增强/i.test(merged);
}

const formatShortTitle = (title) => {
  if (!title) return "未命名";
  const cleanTitle = title.replace(/\.[^/.]+$/, "");
  return cleanTitle.length > 5 ? cleanTitle.substring(0, 5) + "…" : cleanTitle;
};

/**
 * 🆕 描述词的控制台显示截断（纯截断，不去扩展名）
 * ⚠️ 不能复用 formatShortTitle：它那句 replace(/\.[^/.]+$/) 是给文件名去扩展名的，
 *    描述词里正常的 ".xxx" 结尾会被误删一截。
 * 注：这里只影响控制台那行灰字；发给模型的描述词是完整原文（见 useNodeActions.js）。
 */
const formatShortDesc = (s, max = 3) => {
  const t = String(s || '').trim();
  return t.length > max ? t.substring(0, max) + "…" : t;
};

/**
 * 根据比例字符串计算节点预览尺寸（与生成后逻辑一致：SHORT_SIDE = 240）
 * 支持多种格式：
 *   - 比例格式：  "16:9"、"9:16"、"1:1"、"4:3"
 *   - 像素格式：  "1024x1024"、"1536x1024"、"1024x1536"
 *   - 文字格式：  "portrait"、"landscape"
 * @param {string} ratio - 比例或尺寸字符串
 * @returns {{ width: number, height: number } | null}
 */
const ratioToPreviewSize = (ratio) => {
  if (!ratio) return null;
  const raw = String(ratio).toLowerCase();
  if (raw === 'portrait') ratio = '9:16';
  if (raw === 'landscape') ratio = '16:9';

  let w, h;
  // 优先匹配像素格式 WxH（如 1024x1536）
  const pxMatch = String(ratio).match(/^(\d+)\s*[xX×]\s*(\d+)$/);
  if (pxMatch) {
    w = parseFloat(pxMatch[1]);
    h = parseFloat(pxMatch[2]);
  } else {
    // 比例格式 W:H
    const parts = String(ratio).split(':');
    if (parts.length !== 2) return null;
    w = parseFloat(parts[0]);
    h = parseFloat(parts[1]);
  }

  if (!w || !h || w <= 0 || h <= 0) return null;
  const SHORT = 240;
  if (w >= h) {
    // 横屏或正方形：高度固定 240
    return { width: Math.round(w * (SHORT / h)), height: SHORT };
  }
  // 竖屏：宽度固定 240
  return { width: SHORT, height: Math.round(h * (SHORT / w)) };
};

// 根据当前节点尺寸和新比例计算预览尺寸
// 分镜草图节点：固定短边
// 普通节点：固定长边
const ratioToPreviewSizeWithCurrent = (ratio, currentSize, isStoryboard = false) => {
  if (!ratio || !currentSize) return null;

  const raw = String(ratio).toLowerCase();
  if (raw === 'portrait') ratio = '9:16';
  if (raw === 'landscape') ratio = '16:9';

  let w, h;
  const pxMatch = String(ratio).match(/^(\d+)\s*[xX×]\s*(\d+)$/);
  if (pxMatch) {
    w = parseFloat(pxMatch[1]);
    h = parseFloat(pxMatch[2]);
  } else {
    const parts = String(ratio).split(':');
    if (parts.length !== 2) return null;
    w = parseFloat(parts[0]);
    h = parseFloat(parts[1]);
  }

  if (!w || !h || w <= 0 || h <= 0) return null;

  // 统一使用短边固定 240 的策略，与 calculateLongSide120 保持一致
  const SHORT = 240;

  if (isStoryboard) {
    // 分镜草图：固定短边（保持原逻辑）
    const currentW = currentSize.width || 320;
    const currentH = currentSize.height || 240;
    const currentShort = Math.min(currentW, currentH);
    if (w >= h) {
      // 新比例是横屏：短边是高
      return { width: Math.round(currentShort * (w / h)), height: currentShort };
    } else {
      // 新比例是竖屏：短边是宽
      return { width: currentShort, height: Math.round(currentShort * (h / w)) };
    }
  } else {
    // 普通节点：固定短边 300（与导入图片逻辑统一）
    if (w >= h) {
      // 横屏：短边是高，固定 300
      return { width: Math.round(SHORT * (w / h)), height: SHORT };
    } else {
      // 竖屏：短边是宽，固定 300
      return { width: SHORT, height: Math.round(SHORT * (h / w)) };
    }
  }
};


const PARAM_LABEL_MAP = {
  aspect_ratio: '比例',
  image_size: '尺寸',
  quality: '质量',
  version: '版本',
  duration: '时长',
  size: '尺寸',
  orientation: '画面方向',
  watermark: '水印模式',
  private: '私有模式',
  template_id: '运镜',
  camera_strength: '强度',
  mode: '画质',
  audio: '音效',
  negative_prompt: '负面提示词',
  resolution: '分辨率',
  prompt_optimizer: '提示词优化',
  camera_movement: '运镜指令',
  sequentialImageGeneration: '连续图片生成',
  maxImages: '生成数量',
  toolsType: '工具类型',
  thinkingMode: '思考模式',
  promptExtend: '提示词智能改写',
  promptExtendMode: '改写方式',
  enableThinking: 'Thinking 模式',
  webSearch: '联网搜索增强',
  returnLastFrame: '返回尾帧图片',
  ratio: '视频宽高比',
  generate_audio: '生成音频',
  real_person_mode: '真人模式',
  conversion_slots: '真人素材资产化',
  return_last_frame: '返回尾帧图片',
  realPersonMode: '真人模式',
  conversionSlots: '真人素材资产化',
  lora: 'LoRA 模型',
  lora_strength: 'LoRA 强度',
  outputFormat: '输出格式',
  cfg_scale: '提示词相关性',
  multi_shot: '多镜头视频',
  shot_type: '分镜方式'
};

const PARAM_OPTION_LABEL_MAP = {
  size: {
    'auto': '自动',
    '1024x1024': '方形 1:1',
    '1536x1024': '横版 3:2',
    '1024x1536': '竖版 2:3',
    '2048x2048': '2K 方形',
    '2048x1152': '2K 横版',
    '3840x2160': '4K 横版',
    '2160x3840': '4K 竖版'
  },
  aspect_ratio: {
    portrait: '竖屏',
    landscape: '横屏',
    '9:16': '9:16',
    '16:9': '16:9'
  },
  orientation: {
    portrait: '竖屏',
    landscape: '横屏'
  },
  private: {
    true: '私有（不公开）',
    false: '公开（可二次编辑）'
  },
  camera_strength: {
    weak: '弱',
    medium: '中',
    strong: '强'
  },
  mode: {
    'std': '标清 720P',
    'pro': '高清 1080P'
  },
  audio: {
    'true': '开',
    'false': '关'
  },
  resolution: {
    '768p': '标清 768P',
    '1080p': '高清 1080P'
  },
  prompt_optimizer: {
    'true': '开',
    'false': '关'
  },
  watermark: {
    'true': '开',
    'false': '关'
  },
  sequentialImageGeneration: {
    'disabled': '关闭',
    'auto': '自动'
  },
  toolsType: {
    'none': '无',
    'web_search': '联网搜索'
  },
  promptExtendMode: {
    'direct': '直接改写',
    'agent': '智能体改写'
  },
  webSearch: {
    'true': '开启',
    'false': '关闭',
    true: '开启',
    false: '关闭'
  },
  returnLastFrame: {
    'true': '是',
    'false': '否'
  },
  conversionSlots: {
    'all': '全部',
    'firstFrameUrl': '仅首帧',
    'lastFrameUrl': '仅尾帧'
  },
  outputFormat: {
    'jpeg': 'JPEG',
    'png': 'PNG',
    'webp(lossless)': 'WebP (无损)',
    'webp(lossy)': 'WebP (有损)'
  },
  shot_type: {
    'customize': '自定义',
    'intelligence': '智能'
  }
};

const getParamLabel = (paramKey) => PARAM_LABEL_MAP[paramKey] || paramKey;
const getParamOptionLabel = (paramKey, value) => PARAM_OPTION_LABEL_MAP[paramKey]?.[value] || value;

// 🎬 运镜指令分组定义（MiniMax Hailuo 2.3）
const CAMERA_MOVEMENT_GROUPS = [
  { label: '平移', items: ['左移', '右移'] },
  { label: '左右摇', items: ['左摇', '右摇'] },
  { label: '推拉', items: ['推进', '拉远'] },
  { label: '升降', items: ['上升', '下降'] },
  { label: '上下摇', items: ['上摇', '下摇'] },
  { label: '变焦', items: ['变焦推近', '变焦拉远'] },
  { label: '其他', items: ['晃动', '跟随', '固定'] },
];

/**
 * 运镜指令选择面板：多选浮层 + 确定按钮，选中后插入 prompt 开头
 */
function CameraMovementPanel({ prompt, onInsert, activeId, setActiveId }) {
  const panelId = 'camera-movement-panel';
  const isOpen = activeId === panelId;
  const [selected, setSelected] = React.useState([]);
  const [hoveredItem, setHoveredItem] = React.useState(null);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (isOpen) {
      setActiveId(null);
    } else {
      setSelected([]); // 每次打开清空选择
      setActiveId(panelId);
    }
  };

  const handleSelect = (item, e) => {
    e.stopPropagation();
    setSelected(prev =>
      prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]
    );
  };

  const handleConfirm = (e) => {
    e.stopPropagation();
    if (selected.length === 0) return;
    const prefix = selected.map(s => `[${s}]`).join('');
    const currentPrompt = prompt || '';
    onInsert(prefix + currentPrompt);
    setSelected([]);
    setActiveId(null);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelected([]);
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={handleToggle}
        onMouseDown={(e) => e.stopPropagation()}
        title="运镜指令"
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
          fontSize: 13, color: 'var(--text-secondary)',
          background: isOpen ? 'var(--bg-hover)' : 'transparent',
          border: 'none', userSelect: 'none', maxWidth: 180,
          transition: 'background 0.2s, color 0.2s',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          运镜指令
        </span>
        <span style={{ fontSize: 10, opacity: 0.5, flexShrink: 0 }}>▼</span>
      </button>

      {isOpen && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
            background: 'var(--toolbar-bg)', border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8, padding: '8px 10px', zIndex: 1002,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: 280,
          }}
        >
          {/* 标题 + 清除 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>运镜指令（可多选）</span>
            {selected.length > 0 && (
              <button
                onClick={handleClear}
                style={{
                  fontSize: 11, color: 'var(--text-secondary)', background: 'none', border: 'none',
                  cursor: 'pointer', padding: '2px 6px', borderRadius: 4,
                }}
                onMouseEnter={(e) => { e.target.style.color = '#f87171'; }}
                onMouseLeave={(e) => { e.target.style.color = 'var(--text-secondary)'; }}
              >
                清除
              </button>
            )}
          </div>

          {/* 分组列表 */}
          {CAMERA_MOVEMENT_GROUPS.map((group) => (
            <div key={group.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: '#64748b', width: 48, flexShrink: 0, textAlign: 'right' }}>
                {group.label}
              </span>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {group.items.map((item) => {
                  const isSelected = selected.includes(item);
                  const isHovered = hoveredItem === item;
                  return (
                    <button
                      key={item}
                      onClick={(e) => handleSelect(item, e)}
                      onMouseEnter={() => setHoveredItem(item)}
                      onMouseLeave={() => setHoveredItem(null)}
                      style={{
                        padding: '3px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                        border: isSelected ? '1px solid rgba(59,130,246,0.4)' : '1px solid rgba(255,255,255,0.08)',
                        background: isSelected ? 'rgba(59,130,246,0.2)' : (isHovered ? 'rgba(255,255,255,0.08)' : 'transparent'),
                        color: isSelected ? '#60a5fa' : 'var(--node-toolbar-btn)',
                        transition: 'all 0.15s',
                      }}
                    >
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* 确定按钮 */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
            <button
              onClick={handleConfirm}
              style={{
                padding: '4px 16px', borderRadius: 4, fontSize: 12, cursor: selected.length > 0 ? 'pointer' : 'default',
                border: 'none',
                background: selected.length > 0 ? 'rgba(59,130,246,0.6)' : 'rgba(255,255,255,0.06)',
                color: selected.length > 0 ? '#fff' : '#64748b',
                transition: 'all 0.15s',
              }}
            >
              确定{selected.length > 0 ? ` (${selected.length})` : ''}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// 🎨 色板定义（与 TextNode 一致）
const BG_COLORS = [
  '#1e293b', // Slate (默认深蓝灰)
  '#262626', // Neutral (中性深黑灰)
  '#3e2626', // Red tint (暗褐红灰)
  '#3e3226', // Brown tint (暗咖啡灰)
  '#2e3626', // Green tint (暗橄榄灰)
  '#26303e', // Blue tint (暗普鲁士蓝灰)
  '#2e263e', // Purple tint (暗茄紫灰)
  '#3e2636', // Pink tint (暗胭脂灰)
];

// 工具栏按钮组件
const ToolbarButton = ({ active, onClick, title, children }) => {
  return (
    <ToolBtn onClick={onClick} title={title} active={active}>
      {children}
    </ToolBtn>
  );
};

const Divider = () => (
  <div style={{ width: '1px', height: '14px', background: 'rgba(255,255,255,0.15)', margin: '0 4px' }} />
);

// 工具栏内容组件（完整复制 TextNode）
const ToolbarContent = ({ 
  fontSize, fontWeight, isIndented, showColorPicker, toggleColorPicker, 
  bgColor, setPreset, toggleIndent, insertDivider, insertNumbering, 
  copyText, updateBgColor 
}) => (
  <>
    <QuickTooltip content="背景颜色" position="top">
      <div 
        onClick={toggleColorPicker} 
        style={{
          width: '28px', height: '28px', borderRadius: '6px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: showColorPicker ? 'var(--bg-hover)' : 'transparent',
          position: 'relative',
          transition: 'all 0.1s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
        onMouseLeave={(e) => !showColorPicker && (e.currentTarget.style.background = 'transparent')}
      >
        <div style={{
          width: '14px', height: '14px', borderRadius: '50%',
          background: bgColor,
          border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 0 4px rgba(0,0,0,0.3)'
        }} />

        {showColorPicker && (
          <div style={{
            position: 'absolute', top: '36px', left: '50%', transform: 'translateX(-50%)',
            display: 'flex', flexDirection: 'column', gap: '8px', padding: '8px', 
            background: 'var(--bg-panel)', borderRadius: '50px', border: '1px solid var(--border-strong)',
            boxShadow: 'var(--shadow-panel)', zIndex: 2000
          }} onClick={e => e.stopPropagation()}>
            {BG_COLORS.map(c => (
              <div 
                key={c}
                onClick={() => updateBgColor(c)}
                style={{
                  width: '20px', height: '20px', borderRadius: '50%',
                  background: c,
                  border: bgColor === c ? '2px solid var(--text-primary)' : '1px solid rgba(255,255,255,0.2)',
                  cursor: 'pointer', flexShrink: 0, transition: 'transform 0.1s'
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              />
            ))}
            {/* 自定义颜色按钮 */}
            <InlineColorPicker
              color={bgColor}
              rainbow
              size={20}
              onChange={c => updateBgColor(c)}
            />
          </div>
        )}
      </div>
    </QuickTooltip>
    <Divider />

    <ToolbarButton active={fontSize === 24} onClick={() => setPreset('h1')} title="标题 1"><Heading1 size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={fontSize === 20} onClick={() => setPreset('h2')} title="标题 2"><Heading2 size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={fontSize === 16} onClick={() => setPreset('h3')} title="标题 3"><Heading3 size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={fontSize === 12 && fontWeight === 'normal'} onClick={() => setPreset('p')} title="正文"><Pilcrow size={16} strokeWidth={1.5} /></ToolbarButton>

    <Divider />

    <ToolbarButton active={isIndented} onClick={toggleIndent} title="首行缩进"><Indent size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={false} onClick={insertNumbering} title="自动编号"><ListOrdered size={18} strokeWidth={1.5} /></ToolbarButton>
    <ToolbarButton active={false} onClick={insertDivider} title="插入横线"><Minus size={18} strokeWidth={1.5} /></ToolbarButton>

    <Divider />

    <ToolbarButton active={false} onClick={copyText} title="复制全部"><Copy size={16} strokeWidth={1.5} /></ToolbarButton>
  </>
);

// 🆕 自定义下拉选择组件
const DropdownSelector = ({ id, activeId, setActiveId, value, options, onChange, placeholder, title, showPlaceholderAsOption = false, showLogo = false, onOpen = null }) => {
  const containerRef = useRef(null);
  const isOpen = activeId === id;

  // 点击容器外（含画布空白处）收起下拉框。
  // 节点在 React Flow 变换后的视口内，position:fixed 遮罩兜不住画布空白，
  // 改用文档级 mousedown 判断是否点在容器外。
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setActiveId(null);
    };
    // 捕获阶段：React Flow 画布 pane 会在 mousedown 目标阶段 stopPropagation（d3-drag 平移），
    // 冒泡阶段的 document 监听收不到画布空白处的点击，必须用 capture=true 抢在前面。
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [isOpen, setActiveId]);

  const selectedOption = options.find(o => String(o.value) === String(value));
  const displayLabel = selectedOption ? selectedOption.label : placeholder;
  const displayLogo = showLogo ? selectedOption?.logo : null; // 🆕 只在 showLogo=true 时显示

  return (
    <div 
      ref={containerRef}
      className="nodrag"
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={(e) => { 
        e.stopPropagation();
        if (isOpen) {
          setActiveId(null);
          return;
        }
        setActiveId(id);
        if (typeof onOpen === 'function') onOpen();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <QuickTooltip content={isOpen ? null : title} delay={300}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 8px',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 13,
            color: (value !== undefined && value !== null && value !== '') ? 'var(--text-body)' : 'var(--text-secondary)',
            transition: 'background 0.2s, color 0.2s',
            background: isOpen ? 'var(--bg-hover)' : 'transparent',
            border: 'none',
            userSelect: 'none',
            maxWidth: 240
          }}
          onMouseEnter={e => !isOpen && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
          onMouseLeave={e => !isOpen && (e.currentTarget.style.background = 'transparent')}
        >
          {/* 🆕 只在 showLogo=true 时显示 Logo */}
          {showLogo && (
            displayLogo ? (
              <img 
                src={displayLogo} 
                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} 
                alt="" 
              />
            ) : null
          )}
          {showLogo && (
            <Box size={16} style={{ opacity: 0.5, flexShrink: 0, display: displayLogo ? 'none' : 'block' }} />
          )}
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayLabel}</span>
          <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
      </QuickTooltip>

      {isOpen && (
        <div
          className="nowheel nodrag"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            marginBottom: 8,
            background: 'var(--toolbar-bg)',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 8,
            minWidth: showLogo ? 280 : 140,
            maxWidth: 320,
            width: 'max-content',
            padding: '6px',
            zIndex: 1002,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {/* 只有在指定需要显示占位符为选项，且当前没选占位符时才显示 */}
          {showPlaceholderAsOption && value !== "" && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
                setActiveId(null);
              }}
              style={{
                padding: '8px 8px',
                fontSize: 13,
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                borderRadius: 6,
                display: 'flex',
                alignItems: 'center',
                gap: 12
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              {showLogo && <Box size={24} style={{ opacity: 0.3, flexShrink: 0 }} />}
              <span>{placeholder}</span>
            </div>
          )}
          {options.map(opt => {
            const isSelected = String(opt.value) === String(value);
            return (
              <OptionItem 
                key={opt.value} 
                opt={opt} 
                isSelected={isSelected} 
                showLogo={showLogo} 
                onChange={onChange} 
                setActiveId={setActiveId} 
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// 🆕 专门处理选项悬停展示描述的子组件
const OptionItem = ({ opt, isSelected, showLogo, onChange, setActiveId }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onChange(opt.value);
        setActiveId(null);
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        padding: '8px 8px',
        cursor: 'pointer',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: isSelected ? 'rgba(59, 130, 246, 0.15)' : (isHovered ? 'rgba(255,255,255,0.08)' : 'transparent'),
        border: isSelected ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid transparent',
        transition: 'background 0.2s'
      }}
    >
      {showLogo && (
        <div style={{ width: 32, height: 32, display: 'flex', alignItems:'center', justifyContent:'center', background: opt.logo ? 'transparent' : 'rgba(255,255,255,0.05)', borderRadius: 4, flexShrink: 0, position: 'relative' }}>
          {opt.logo && (
            <img 
              src={opt.logo} 
              onError={(e) => { 
                e.target.style.display = 'none'; 
                e.target.parentElement.style.background = 'rgba(255,255,255,0.05)';
                e.target.nextSibling.style.display = 'flex';
              }}
              style={{ width: 32, height: 32, objectFit: 'contain', borderRadius: 4 }} 
              alt="" 
            />
          )}
          <Box size={16} style={{ opacity: 0.5, display: opt.logo ? 'none' : 'flex', position: opt.logo ? 'absolute' : 'static' }} />
        </div>
      )}
      
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1 }}>
         <div style={{ fontSize: 13, color: isSelected ? '#60a5fa' : 'var(--text-body)', fontWeight: 500 }}>{opt.label}</div>
         {showLogo && opt.description && (
            <div style={{ 
              fontSize: 11,
              color: 'var(--text-secondary)',
              opacity: (isHovered || isSelected) ? 1 : 0,
              maxHeight: (isHovered || isSelected) ? '40px' : '0px',
              marginTop: (isHovered || isSelected) ? 4 : 0,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis'
            }}>
              {opt.description}
            </div>
         )}
      </div>
    </div>
  );
};

// 🎨 预设下拉（仅展示当前工具集内预设，按分组分层；顶部含「关闭不启用」开关）
//   - 顶部按钮显示 工具集名 · 选中预设名；展开后第一行是启用/关闭开关，下面按分组列出预设
//   - disabled=true 时按钮置灰，下拉仍可打开（用于重新启用 / 切换预设）
//   - 复用 activeDropdown 统一开关管理（与其它下拉互斥）
const PresetDropdown = ({ id, activeId, setActiveId, toolset, value, disabled, onChange, onToggleDisabled }) => {
  const isOpen = activeId === id;
  const groups = (toolset?.groups || []);
  let selected = null;
  for (const g of groups) { const f = (g.presets || []).find(p => String(p.id) === String(value)); if (f) { selected = f; break; } }
  const headLabel = selected ? selected.name : (toolset?.name || '预设');

  return (
    <div
      className="nodrag"
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={(e) => { e.stopPropagation(); setActiveId(isOpen ? null : id); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <QuickTooltip content={isOpen ? null : '预设工具箱'} delay={300}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6,
          cursor: 'pointer', fontSize: 13, color: disabled ? 'var(--text-muted)' : (selected ? 'var(--text-body)' : 'var(--text-secondary)'),
          background: isOpen ? 'var(--bg-hover)' : 'transparent', userSelect: 'none', maxWidth: 180,
          opacity: disabled ? 0.6 : 1, transition: 'background 0.2s, color 0.2s',
        }}
          onMouseEnter={e => !isOpen && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
          onMouseLeave={e => !isOpen && (e.currentTarget.style.background = 'transparent')}>
          {selected?.logoUrl ? (
            <img src={selected.logoUrl} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} alt="" />
          ) : null}
          <Wand2 size={15} style={{ flexShrink: 0, opacity: 0.7, display: selected?.logoUrl ? 'none' : 'block' }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: disabled ? 'line-through' : 'none' }}>{headLabel}</span>
          <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
      </QuickTooltip>

      {isOpen && (
        <div className="nowheel nodrag custom-scrollbar" style={{
          position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
          background: 'var(--toolbar-bg)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
          minWidth: 220, maxWidth: 300, padding: 6, zIndex: 1002, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          maxHeight: 380, overflowY: 'auto',
        }}>
          {/* 顶部开关：关闭不启用预设 */}
          <div
            onClick={(e) => { e.stopPropagation(); onToggleDisabled?.(!disabled); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
              background: 'var(--bg-hover)', marginBottom: 6, border: '1px solid var(--border-subtle)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-hover)'}>
            {/* 开关样式：启用=绿，关闭=灰 */}
            <div style={{ width: 30, height: 16, borderRadius: 10, background: disabled ? 'var(--border-strong)' : '#10b981', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: disabled ? 2 : 16, transition: 'left 0.2s' }} />
            </div>
            <span style={{ fontSize: 12, color: disabled ? 'var(--text-secondary)' : 'var(--text-body)' }}>{disabled ? '预设已关闭（生图不注入）' : '预设已启用'}</span>
          </div>

          {groups.map(g => (
            <div key={g.id} style={{ marginBottom: 4, opacity: disabled ? 0.5 : 1 }}>
              <div style={{ fontSize: 9, color: '#64748b', padding: '4px 8px 2px', fontWeight: 600, letterSpacing: '0.5px' }}>{g.name}</div>
              {(g.presets || []).map(p => {
                const isSel = String(p.id) === String(value);
                return (
                  <div key={p.id}
                    onClick={(e) => { e.stopPropagation(); onChange(p.id); setActiveId(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
                      background: isSel ? 'rgba(59,130,246,0.15)' : 'transparent', border: isSel ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                    {p.logoUrl ? (
                      <img src={p.logoUrl} alt={p.name} style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                    ) : (<Wand2 size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />)}
                    <span style={{ fontSize: 13, color: isSel ? '#60a5fa' : 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ✨ 技能工具箱下拉框（三层结构：工具集 → 分组 → 技能，紫色主题）
const SkillboxDropdown = ({ id, activeId, setActiveId, toolset, value, disabled, onChange, onToggleDisabled }) => {
  const isOpen = activeId === id;
  const groups = (toolset?.groups || []);
  let selected = null;
  for (const g of groups) { const f = (g.presets || []).find(p => String(p.id) === String(value)); if (f) { selected = f; break; } }
  const headLabel = selected ? selected.name : (toolset?.name || '技能');

  return (
    <div
      className="nodrag"
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={(e) => { e.stopPropagation(); setActiveId(isOpen ? null : id); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <QuickTooltip content={isOpen ? null : '技能工具箱'} delay={300}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6,
          cursor: 'pointer', fontSize: 13, color: disabled ? 'var(--text-muted)' : (selected ? 'var(--text-body)' : 'var(--text-secondary)'),
          background: isOpen ? 'var(--bg-hover)' : 'transparent', userSelect: 'none', maxWidth: 180,
          opacity: disabled ? 0.6 : 1, transition: 'background 0.2s, color 0.2s',
        }}
          onMouseEnter={e => !isOpen && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
          onMouseLeave={e => !isOpen && (e.currentTarget.style.background = 'transparent')}>
          {selected?.logoUrl ? (
            <img src={selected.logoUrl} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} alt="" />
          ) : null}
          <Sparkles size={15} style={{ flexShrink: 0, opacity: 0.7, display: selected?.logoUrl ? 'none' : 'block' }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: disabled ? 'line-through' : 'none' }}>{headLabel}</span>
          <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
      </QuickTooltip>

      {isOpen && (
        <div className="nowheel nodrag custom-scrollbar" style={{
          position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
          background: 'var(--toolbar-bg)', border: '1px solid rgba(168, 85, 247, 0.3)', borderRadius: 8,
          minWidth: 220, maxWidth: 300, padding: 6, zIndex: 1002, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          maxHeight: 380, overflowY: 'auto',
        }}>
          {/* 顶部开关：关闭不启用技能 */}
          <div
            onClick={(e) => { e.stopPropagation(); onToggleDisabled?.(!disabled); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
              background: 'var(--bg-hover)', marginBottom: 6, border: '1px solid var(--border-subtle)' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-hover)'}
            onMouseLeave={e => e.currentTarget.style.background = 'var(--bg-hover)'}>
            <div style={{ width: 30, height: 16, borderRadius: 10, background: disabled ? 'var(--border-strong)' : '#a855f7', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: disabled ? 2 : 16, transition: 'left 0.2s' }} />
            </div>
            <span style={{ fontSize: 12, color: disabled ? 'var(--text-secondary)' : 'var(--text-body)' }}>{disabled ? '技能已关闭（生图不注入）' : '技能已启用'}</span>
          </div>

          {groups.map(g => (
            <div key={g.id} style={{ marginBottom: 4, opacity: disabled ? 0.5 : 1 }}>
              <div style={{ fontSize: 9, color: 'rgba(168, 85, 247, 0.8)', padding: '4px 8px 2px', fontWeight: 600, letterSpacing: '0.5px' }}>{g.name}</div>
              {(g.presets || []).map(p => {
                const isSel = String(p.id) === String(value);
                return (
                  <div key={p.id}
                    onClick={(e) => { e.stopPropagation(); onChange(p.id); setActiveId(null); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
                      background: isSel ? 'rgba(168, 85, 247, 0.15)' : 'transparent', border: isSel ? '1px solid rgba(168, 85, 247, 0.3)' : '1px solid transparent', transition: 'background 0.15s' }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                    {p.logoUrl ? (
                      <img src={p.logoUrl} alt={p.name} style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                    ) : (<Sparkles size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />)}
                    <span style={{ fontSize: 13, color: isSel ? '#a855f7' : 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// 🎨 画面风格下拉框（仅分镜大师节点）——单层列表，默认选择第一个预设
const StyleDropdown = ({ id, activeId, setActiveId, value, onChange }) => {
  const isOpen = activeId === id;
  const [list, setList] = useState(() => (stylePresetCache ? stylePresetCache.list : []));
  const [loading, setLoading] = useState(false);

  // 🆕 组件挂载时立即拉取预设列表（缓存命中则瞬时返回）
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchStoryboardStylePresets().then(l => { if (alive) { setList(l); setLoading(false); } });
    return () => { alive = false; };
  }, []); // 只在挂载时执行一次

  // 🆕 自动选择第一个预设：列表加载完成后，如果当前值为空且列表非空，自动选择第一个
  useEffect(() => {
    if (!loading && list && list.length > 0 && !value) {
      onChange(list[0].id);
    }
  }, [loading, list, value, onChange]);

  const selected = (list || []).find(s => String(s.id) === String(value)) || null;
  const headLabel = selected ? selected.name : '画面风格';

  return (
    <div
      className="nodrag"
      style={{ position: 'relative', display: 'inline-block' }}
      onClick={(e) => { e.stopPropagation(); setActiveId(isOpen ? null : id); }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <QuickTooltip content={isOpen ? null : '画面风格'} delay={300}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6,
          cursor: 'pointer', fontSize: 13, color: selected ? 'var(--text-body)' : 'var(--text-secondary)',
          background: isOpen ? 'var(--bg-hover)' : 'transparent', userSelect: 'none', maxWidth: 180,
          transition: 'background 0.2s, color 0.2s',
        }}
          onMouseEnter={e => !isOpen && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
          onMouseLeave={e => !isOpen && (e.currentTarget.style.background = 'transparent')}>
          {selected?.logoUrl ? (
            <img src={selected.logoUrl} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
              style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 2, flexShrink: 0 }} alt="" />
          ) : null}
          <Sparkles size={15} style={{ flexShrink: 0, opacity: 0.7, display: selected?.logoUrl ? 'none' : 'block' }} />
          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{headLabel}</span>
          <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
      </QuickTooltip>

      {isOpen && (
        <div className="nowheel nodrag custom-scrollbar" style={{
          position: 'absolute', bottom: '100%', left: 0, marginBottom: 8,
          background: 'var(--toolbar-bg)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
          minWidth: 140, maxWidth: 200, padding: 6, zIndex: 1002, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          maxHeight: 360, overflowY: 'auto',
        }}>
          {loading && (list || []).length === 0 && (
            <div style={{ fontSize: 12, color: '#64748b', padding: '8px' }}>加载中…</div>
          )}
          {!loading && (list || []).length === 0 && (
            <div style={{ fontSize: 12, color: '#64748b', padding: '8px' }}>暂无画面风格</div>
          )}
          {(list || []).map(s => {
            const isSel = String(s.id) === String(value);
            return (
              <div key={s.id}
                onClick={(e) => { e.stopPropagation(); onChange(s.id); setActiveId(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 8px', borderRadius: 6, cursor: 'pointer',
                  background: isSel ? 'rgba(59,130,246,0.15)' : 'transparent', border: isSel ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent', transition: 'background 0.15s' }}
                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}>
                {s.logoUrl ? (
                  <img src={s.logoUrl} alt={s.name} style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                ) : (<Sparkles size={16} color="var(--text-secondary)" style={{ flexShrink: 0 }} />)}
                <span style={{ fontSize: 13, color: isSel ? '#60a5fa' : 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name || '未命名风格'}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ShimmerThumb = ({ src, fallbackSrc, isVideo, style }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  // 实际加载的地址：优先本地缓存，失败后回退到原始 url
  const [curSrc, setCurSrc] = useState(src);
  const triedFallback = useRef(false);
  const mediaRef = useRef(null);

  // src 变化（节点数据刷新/切换）时重置加载状态，重新走一遍缓存→回退流程
  useEffect(() => {
    setCurSrc(src);
    setLoaded(false);
    setFailed(false);
    triedFallback.current = false;
  }, [src]);

  // 🛡️ 缓存命中补判：当 curSrc 指向的图/视频已在浏览器缓存中解码完成时，
  //   <img> onLoad / <video> onLoadedData 可能不会再触发（尤其 src 从 A→B 切换、
  //   B 已缓存的情况），导致 loaded 永远停在 false、shimmer 一直转
  //   （折叠控制台再展开会整体重挂载 <img> 才恢复）。这里在挂载/curSrc 变化后
  //   主动检查已完成状态，命中即判定加载完成，不依赖事件。
  useEffect(() => {
    const el = mediaRef.current;
    if (!el) return;
    if (isVideo ? el.readyState >= 2 : (el.complete && el.naturalWidth > 0)) {
      setLoaded(true);
    }
  }, [curSrc, isVideo]);

  const handleError = () => {
    // 首次失败且有可回退的原始地址（如 localCacheUrl 刷新后失效）→ 换成原始 url 再试一次
    if (!triedFallback.current && fallbackSrc && fallbackSrc !== curSrc) {
      triedFallback.current = true;
      setLoaded(false);
      setCurSrc(fallbackSrc);
    } else {
      // 彻底失败：显示裂图占位，容器靠 minWidth 撑住，不会塌成竖线
      setFailed(true);
    }
  };

  if (failed) {
    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.04)', borderRadius: 4 }}>
        <ImageOff size={16} color="rgba(255,255,255,0.3)" />
      </div>
    );
  }

  return (
    <>
      {!loaded && <div className="shimmer-wrapper" style={{ position: 'absolute', inset: 0, borderRadius: 4 }} />}
      {isVideo
        ? <video ref={mediaRef} src={curSrc} style={{ ...style, opacity: loaded ? 1 : 0, transition: 'opacity 0.25s' }} preload="metadata" muted playsInline onLoadedData={() => setLoaded(true)} onError={handleError} />
        : <img ref={mediaRef} src={curSrc} style={{ ...style, opacity: loaded ? 1 : 0, transition: 'opacity 0.25s' }} alt="" onLoad={() => setLoaded(true)} onError={handleError} />
      }
    </>
  );
};

// --- 最终兼容性修复：数据传输 ---
const handleDragStart = (e, type, url, title) => {
  e.stopPropagation(); // 阻止 React Flow 劫持
  
  // 构造标准 JSON 字符串
  const payload = JSON.stringify({ type, url, title: title || '未命名资产' });
  
  // 必须设置 text/plain 才能跨区域拖拽
  e.dataTransfer.setData('text/plain', payload);
  e.dataTransfer.effectAllowed = 'copy';
};

// 参考素材卡片左下角序号角标的高度。右下角的描述词开关圆钮共用这个值，
// 两者严格等高 —— 圆钮比序号高一截看着很别扭。
const REF_BADGE_H = 13;

/**
 * 🎵 音频引用缩略图卡片
 * - 显示音频标题 + 播放按钮 + 编号标签
 * - 支持拖拽到画布创建新音频节点
 * - 播放按钮在右下角，编号标签在左下角（橙色，匹配音频节点 logo）
 * @param {Object} audioObj - 音频数据 { url, localCacheUrl, title, id }
 * @param {number} index - 显示的编号（1-based）
 * @param {Function} onRemove - 删除回调
 */
const AudioCardTile = ({ audioObj, index, onRemove, onToggleDesc }) => {
  const audioRef = useRef(null);
  // 三态：idle / loading / playing
  const [playState, setPlayState] = useState('idle');
  const isPlaying = playState === 'playing';
  const isLoading = playState === 'loading';

  const toggle = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying || isLoading) {
      audio.pause();
      setPlayState('idle');
    } else {
      setPlayState('loading');
      audio.play().catch(() => setPlayState('idle'));
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onPlaying = () => setPlayState('playing');
    const onEnded   = () => setPlayState('idle');
    const onPause   = () => setPlayState(prev => prev === 'loading' ? prev : 'idle');
    audio.addEventListener('playing', onPlaying);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    return () => { audio.removeEventListener('playing', onPlaying); audio.removeEventListener('ended', onEnded); audio.removeEventListener('pause', onPause); };
  }, []);

  // 🆕 本地优先：优先使用 blob URL 缓存（避免每次走云存储远程加载）
  const audioSrc = audioObj.localCacheUrl || audioObj.url;
  return (
    <div
      className="nodrag"
      style={styles.textItem}
      draggable="true"
      onDragStart={(e) => handleDragStart(e, 'audio', audioObj.url, audioObj.title)}
    >
      <audio ref={audioRef} src={audioSrc} />
      <div style={styles.audioTile}>
        <Music size={14} color="rgba(255,255,255,0.2)" />
        <div style={styles.textTileTitle}>{audioObj.title || "音频资产"}</div>
        {/* 🔧 播放按钮移到左上角（避免与右下角 T 角标重叠） */}
        <div onClick={toggle} style={{ position: 'absolute', top: '4px', left: '4px', width: '20px', height: '20px', borderRadius: '4px', background: isLoading ? 'rgba(59,130,246,0.25)' : isPlaying ? 'rgba(34,197,94,0.25)' : 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.2s', backdropFilter: 'blur(4px)', zIndex: 10 }}>
          {isLoading
            ? <Loader2 size={10} color="#3b82f6" style={{ animation: 'spin 1s linear infinite' }} />
            : isPlaying
              ? <Pause size={10} color="#22c55e" fill="#22c55e" />
              : <Play size={10} color="#fff" fill="#fff" />
          }
        </div>
        {/* 🆕 描述词开关（右下角）：与图像/视频卡片同一套逻辑 */}
        {/* 🔧 临时调试：始终显示角标，方便排查问题 */}
        <div
          style={{
            ...descBadgeStyle(REF_BADGE_H, audioObj.descEnabled !== false),
            position: 'absolute', bottom: 0, right: 0, zIndex: 11,
          }}
          title={audioObj.descEnabled !== false
            ? `描述词已进提示词（点击关闭）${audioObj.description ? `：${audioObj.description}` : '(当前资产无描述词)'}`
            : `描述词未进提示词（点击开启）${audioObj.description ? `：${audioObj.description}` : '(当前资产无描述词)'}`}
          onClick={(e) => {
            e.stopPropagation();
            console.log('[T角标] 点击切换，当前音频:', audioObj);
            onToggleDesc && onToggleDesc(audioObj.id);
          }}
          onMouseEnter={descBadgeHover.onMouseEnter(audioObj.descEnabled !== false)}
          onMouseLeave={descBadgeHover.onMouseLeave(audioObj.descEnabled !== false)}
        >
          <DescriptionIcon size={9} />
        </div>
      </div>
      <div style={styles.indexBadge('#f97316')}>{index}</div>
      <div style={styles.closeBtn} onClick={() => onRemove()}><X size={10} color="#fff" /></div>
    </div>
  );
};

// 🆕 全屏 Prompt 编辑器 Modal
// 🔧 改用 SlateStoryboardEditor，复用分镜大师的完整 @ 下拉逻辑（Portal + 动态位置）
const FullScreenPromptModal = ({ isOpen, onClose, prompt, slateValue, assets, onChange, toolbarProps, textPolishEnabled, onPolish, isPolishing }) => {
  const fsEditorRef = useRef(null);
  // 当前文本直接取 prompt：每次输入都会写回 data.prompt，编辑器聚焦时也不会被外部值打断
  const localText = prompt || '';

  // 打开时聚焦并把光标放到末尾
  useEffect(() => {
    if (isOpen) fsEditorRef.current?.focusEnd?.();
  }, [isOpen]);

  // 🆕 全屏编辑器 placeholder 颜色自适应
  useEffect(() => {
    const styleId = 'fullscreen-prompt-modal-fix';
    let style = document.getElementById(styleId);
    if (!style && isOpen) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }

    if (style && isOpen) {
      // 使用 CSS 变量 var(--sb-cell-placeholder)，自适应亮/暗模式（类名提高优先级，压过 Slate 全局样式）
      style.textContent = `
        .fullscreen-prompt-modal [data-slate-editor] [data-slate-placeholder] {
          color: var(--sb-cell-placeholder, rgba(156, 163, 175, 0.85)) !important;
          opacity: 1 !important;
        }
      `;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleFsChange = (e) => {
    onChange(e.target.value, e.target.slateValue);
  };

  // 语音输入：编辑器聚焦时外部值不会同步进来，直接写编辑器（onChange 会照常回写 data）
  const handleVoiceResult = (newText) => {
    if (fsEditorRef.current?.setText) fsEditorRef.current.setText(newText);
    else onChange(newText);
  };
  
  return createPortal(
    <div
      className="nodrag"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'flex-start', // 🔧 顶对齐，和分镜大师一致
        justifyContent: 'center',
        zIndex: 999999,
        paddingTop: '40px', // 🔧 距离顶部 40px
        paddingBottom: '450px', // 🔧 底部留 450px 给资产列表
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      <div
        style={{
          width: '900px',
          maxWidth: '100%',
          height: '80vh',
          background: 'var(--modal-gradient)',
          borderRadius: '20px',
          boxShadow: '0 25px 100px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border-subtle)',
          overflow: 'hidden', // 🔧 防止内容溢出
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 🔝 Header with Toolbar */}
        <div style={{
          height: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'rgba(0,0,0,0.2)',
          position: 'relative',
          borderRadius: '20px 20px 0 0',
          flexShrink: 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', pointerEvents: 'auto', userSelect: 'none' }}>
            <ToolbarContent {...toolbarProps} />
          </div>
          {/* 📝 润色 + 🎤 全屏模式下的语音输入按钮 + 关闭按钮 */}
          <div style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {textPolishEnabled && (
              <div
                onClick={() => { if (!isPolishing && localText.trim()) onPolish(localText); }}
                style={{
                  width: '36px', height: '36px', borderRadius: '50%',
                  background: isPolishing ? 'rgba(96,165,250,0.2)' : 'var(--bg-hover)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: isPolishing ? 'wait' : localText.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s', opacity: localText.trim() ? 1 : 0.45,
                }}
                onMouseEnter={(e) => { if (!isPolishing) e.currentTarget.style.background = 'rgba(96,165,250,0.25)'; }}
                onMouseLeave={(e) => { if (!isPolishing) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                title="文本整理"
              >
                {isPolishing ? <Loader2 size={18} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={18} color="#60a5fa" />}
              </div>
            )}
            <VoiceMicButton
              size={32}
              position="inline"
              currentText={localText}
              onResult={handleVoiceResult}
              textareaRef={fsEditorRef}
            />
            <div 
              onClick={onClose}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                background: 'var(--bg-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border-strong)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
            >
              <Check size={20} color="var(--text-primary)" />
            </div>
          </div>
        </div>

        {/* 📝 Slate 富文本编辑区：@资产名 显示气泡 + 自动下拉选择 */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', color: 'var(--text-body)', padding: '40px 60px' }}>
          <SlateStoryboardEditor
            value={localText}
            externalSlateValue={slateValue}
            assets={assets}
            onChange={handleFsChange}
            placeholder="在此输入 Prompt 指令..."
            editorRef={fsEditorRef}
            showMentionDropdown={true}
            className="custom-scrollbar"
            style={{
              flex: 1,
              minHeight: '0px',
              maxHeight: 'none',
              background: 'transparent', // 🔧 保持透明，和分镜大师一致
              color: 'var(--text-primary)',
              fontSize: `${toolbarProps.fontSize}px`,
              fontWeight: toolbarProps.fontWeight,
              lineHeight: '1.8',
              padding: '0', // 🔧 padding 已经在外层容器，这里清零
              boxSizing: 'border-box',
              fontFamily: '"PingFang SC", "Microsoft YaHei", sans-serif',
              whiteSpace: 'pre-wrap',
              transition: 'background 0.3s ease',
            }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

const styles = {
  // ⚠️ 宽度写死 520px，不用 fit-content + minWidth：
  //    fit-content 会取 max-content，而 max-content 由横排的素材卡片宽度之和决定。
  //    卡片是 width:auto（宽度由图片长宽比决定），图片加载完成前浏览器不知道长宽比，
  //    只能退到 minWidth:40px —— 等缩略图到了卡片撑宽，控制台跟着变宽，就是那下「刷新」。
  //    refFiles 里没存宽高，加载前没有任何信息能算出该占多宽，所以只能让宽度与内容脱钩。
  //    （这也解释了为什么只有久未打开的节点会跳：最近打开过的缩略图在浏览器缓存里，
  //      解码几乎瞬时，首屏宽度一次就对。）
  //    宽度值 720：原来是 minWidth 520（当下限用），固定后顺势加宽到 720 ——
  //    控制台抗缩放（scale 1/zoom），所以这个数就是屏幕上的实际像素宽度，不随画布缩放变。
  consolePanel: (zoom) => ({ position: 'absolute', top: 'calc(100% + 2px)', left: '50%', transform: `translateX(-50%) scale(${1 / zoom})`, transformOrigin: 'top center', width: '720px', background: 'var(--console-bg)', borderRadius: '15px', border: '0.5px solid var(--console-border)', padding: '16px', zIndex: 1002, boxSizing: 'border-box', boxShadow: '0 20px 50px rgba(0,0,0,0.6)' }),
  // 素材卡片满了自动折到第二行（wrap）。
  // ⚠️ 代价要知道：控制台宽度已固定，但缩略图加载完卡片会变宽 → 每行放的张数变少
  //    → 行数增加 → 控制台往下长一截。也就是说「宽度跳变」变成了「高度跳变」。
  //    权衡结论：宽度跳变会把整个面板左右推移、很刺眼；高度往下长要温和得多，
  //    而且资产必须看全（不能被一行藏住），所以保留 wrap。
  assetRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', width: '100%', marginBottom: '12px', paddingBottom: '4px' },
  imageItem: { position: 'relative', height: '60px', width: 'auto', minWidth: '40px', border: '1px solid var(--border-subtle)', borderRadius: 4, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', cursor: 'grab' },
  thumbImg: { height: '100%', width: 'auto', objectFit: 'contain', pointerEvents: 'none' }, // 关键：图片本身不响应鼠标，只响应容器拖拽
  textItem: { position: 'relative', width: '60px', minWidth: '60px', height: '60px', border: '1px solid var(--border-subtle)', flexShrink: 0, cursor: 'grab' },
  textTile: (bgColor) => ({ width: '100%', height: '100%', backgroundColor: (!bgColor || bgColor === 'transparent') ? '#2a2a2a' : bgColor, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px', boxSizing: 'border-box', gap: '4px', transition: 'background-color 0.3s ease' }),
  audioTile: { width: '100%', height: '100%', background: '#2d3748', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4px', boxSizing: 'border-box', gap: '2px', position: 'relative' },
  textTileTitle: { fontSize: '9px', color: '#fff', fontWeight: 'bold', textAlign: 'center', display: '-webkit-box', WebkitLineClamp: '2', WebkitBoxVertical: 'vertical', overflow: 'hidden', lineHeight: '1.1' },
  // ⚠️ height 显式写成 REF_BADGE_H：原来靠 padding 撑高（9px 字 + 上下 1px ≈ 13px），
  //    但右下角那颗描述词圆钮必须跟它严格等高，靠估算对不齐。两边共用同一个常量。
  indexBadge: (color) => ({ position: 'absolute', bottom: '0px', left: '0px', height: `${REF_BADGE_H}px`, padding: '0 5px', background: color, color: '#fff', fontSize: '9px', fontWeight: '900', zIndex: 10, display: 'flex', alignItems: 'center' }),
  closeBtn: { position: 'absolute', top: '0px', right: '0px', width: '16px', height: '16px', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 11 },
  semanticRow: { background: 'rgba(148, 163, 184, 0.05)', border: '0.5px dashed rgba(148, 163, 184, 0.3)', borderRadius: '6px', padding: '8px 12px', marginBottom: '12px', fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' },
  consoleTextarea: { width: '100%', background: 'var(--console-input-bg)', border: '1px solid var(--console-input-border)', borderRadius: '12px', color: 'var(--console-text)', resize: 'none', outline: 'none', fontSize: '14px', lineHeight: '1.6', overflowY: 'auto', minHeight: '120px', padding: '12px', boxSizing: 'border-box' },
  footer: { display: 'flex', justifyContent: 'flex-start', alignItems: 'center', borderTop: '0.5px solid var(--console-input-border)', paddingTop: '12px', marginTop: '12px' },
  sendBtn: { background: '#3b82f6', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: 'none', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }
};

// 📝 文本润色预览弹窗
const TextPolishPreviewModal = ({ isOpen, originalText, polishedText, isStreaming, onConfirm, onCancel, onRepolish }) => {
  const [localText, setLocalText] = useState(originalText || '');
  const [editedPolished, setEditedPolished] = useState('');
  const userEditedRef = useRef(false);
  const localTextareaRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setLocalText(originalText || '');
      userEditedRef.current = false;
    }
  }, [isOpen, originalText]);

  useEffect(() => {
    if (!userEditedRef.current) setEditedPolished(polishedText || '');
  }, [polishedText]);

  if (!isOpen) return null;
  return createPortal(
    <div className="nodrag" onClick={onCancel} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999999, padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 900, maxWidth: '95vw', height: '92vh', background: 'var(--modal-surface)', borderRadius: 16, border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Wand2 size={16} color="#60a5fa" /> 提示词整理大师
            {isStreaming && <Loader2 size={14} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} />}
          </div>
          <div onClick={onCancel} style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-hover)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <X size={16} color="var(--text-secondary)" />
          </div>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 20, display: 'flex', gap: 16 }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 8, fontWeight: 600 }}>原文</div>
            <div style={{ flex: 1, position: 'relative' }}>
              <textarea
                ref={localTextareaRef}
                data-voice-shortcut="true"
                value={localText}
                onChange={(e) => setLocalText(e.target.value)}
                placeholder="输入或修改文本..."
                spellCheck={false}
                style={{ width: '100%', height: '100%', padding: '12px 12px 40px 12px', background: 'var(--modal-input-bg)', borderRadius: 8, border: '1px solid var(--modal-input-border)', fontSize: 13, color: 'var(--text-body)', lineHeight: 1.6, resize: 'none', outline: 'none', minHeight: 200, fontFamily: 'inherit', boxSizing: 'border-box' }}
              />
              <div style={{ position: 'absolute', right: 8, bottom: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
                <VoiceMicButton
                  size={26}
                  position="inline"
                  currentText={localText}
                  onResult={(newText) => setLocalText(newText)}
                  textareaRef={localTextareaRef}
                />
                <div
                  onClick={() => { if (!isStreaming && localText.trim()) { userEditedRef.current = false; onRepolish(localText); } }}
                  style={{
                    width: 26, height: 26, borderRadius: 6,
                    background: isStreaming ? 'rgba(96,165,250,0.2)' : 'var(--bg-hover)',
                    border: `1px solid ${isStreaming ? 'rgba(96,165,250,0.4)' : 'var(--border-strong)'}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: isStreaming ? 'wait' : localText.trim() ? 'pointer' : 'not-allowed',
                    opacity: localText.trim() ? 1 : 0.45,
                  }}
                  title="重新整理"
                >
                  {isStreaming ? <Loader2 size={13} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={13} color="#60a5fa" />}
                </div>
              </div>
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 11, color: '#60a5fa', marginBottom: 8, fontWeight: 600 }}>整理后（可编辑）</div>
            <textarea
              data-voice-shortcut="true"
              value={editedPolished}
              onChange={(e) => { userEditedRef.current = true; setEditedPolished(e.target.value); }}
              placeholder={isStreaming ? '正在生成...' : ''}
              spellCheck={false}
              style={{ flex: 1, width: '100%', padding: 12, background: 'rgba(96,165,250,0.05)', borderRadius: 8, border: '1px solid rgba(96,165,250,0.15)', fontSize: 13, color: 'var(--text-body)', lineHeight: 1.6, resize: 'none', outline: 'none', minHeight: 200, fontFamily: 'inherit', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onCancel} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>取消</button>
          <button onClick={() => onConfirm(editedPolished)} disabled={isStreaming || !editedPolished.trim()} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: (isStreaming || !editedPolished.trim()) ? '#1e3a5f' : '#2563eb', color: (isStreaming || !editedPolished.trim()) ? '#94a3b8' : '#fff', cursor: (isStreaming || !editedPolished.trim()) ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 500 }}>确认替换</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/**
 * 多模态模型「生成模式」可选项 + 推荐值（Seedance / MiniMax-H3 / HappyHorse）
 *
 * 抽到模块作用域，是为了让「自动重算的 effect」和「下拉渲染」共用同一份判定，
 * 避免两处各写一遍后逐渐走偏。纯函数，不依赖组件状态。
 *
 * @returns {{ availableModes: string[], recommended: string|undefined }}
 */
function computeImageModeOptions({ imgCount, hasAudio, hasVideo, isHappyHorse, configImageModes }) {
  let availableModes;

  if (isHappyHorse) {
    // HappyHorse：有参考视频 → 视频编辑；否则按图/音频数量给参考生视频
    if (hasVideo) {
      availableModes = ['video-edit'];
    } else if (hasAudio || imgCount >= 2) {
      availableModes = ['reference-to-video'];
    } else if (imgCount === 1) {
      availableModes = ['image-to-video', 'reference-to-video'];
    } else {
      return { availableModes: [], recommended: undefined };
    }
  } else {
    // Seedance / MiniMax-H3：支持首尾帧
    //   有音频/视频 或 3+ 张图 → 只能多模态参考
    //   2 张图 → 首尾帧、多模态参考
    //   1 张图 → 单图生视频、多模态参考
    //   0 张图 → 不显示
    if (hasAudio || hasVideo || imgCount >= 3) {
      availableModes = ['multi_reference'];
    } else if (imgCount === 2) {
      availableModes = ['first_last_frame', 'multi_reference'];
    } else if (imgCount === 1) {
      availableModes = ['first_frame', 'multi_reference'];
    } else {
      return { availableModes: [], recommended: undefined };
    }
  }

  // 只保留管理员启用的选项（video-edit 除外，它是自动模式）
  if (!availableModes.includes('video-edit') && Array.isArray(configImageModes)) {
    availableModes = availableModes.filter(m => configImageModes.includes(m));
  }
  if (availableModes.length === 0) return { availableModes: [], recommended: undefined };

  // ── 智能推荐 ──
  let recommended;
  if (isHappyHorse) {
    if (hasVideo) recommended = 'video-edit';
    else if (hasAudio || imgCount >= 2) recommended = 'reference-to-video';
    else if (imgCount === 1) recommended = 'image-to-video';
  } else {
    if (hasAudio || hasVideo || imgCount >= 2) recommended = 'multi_reference';
    else if (imgCount === 1) recommended = 'first_frame';
  }

  // 推荐值可能被上面的「管理员启用项」过滤掉（例：只启用多模态时推荐 first_frame）。
  // 必须收敛到可选集内，否则调用方会把一个不在下拉里的值写进节点。
  if (!recommended || !availableModes.includes(recommended)) {
    recommended = availableModes[0];
  }

  return { availableModes, recommended };
}

// P1: React.memo 防止父级 canvas 重渲染时级联到不相关的节点控制台
export default React.memo(function NodeConsole({ data, id, zoom, handleUpdate, consolePinned, onTogglePin }) {
  const [models, setModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadModelsError, setLoadModelsError] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null); // 🆕 统一管理打开的下拉框 ID
  const [isFullscreenPrompt, setIsFullscreenPrompt] = useState(false); // 🆕 全屏 Prompt 编辑器状态

  // 📝 文本润色状态
  const [isPolishing, setIsPolishing] = useState(false);
  const [polishPreview, setPolishPreview] = useState(null); // { original, polished }
  const [textPolishEnabled, setTextPolishEnabled] = useState(false);

  useEffect(() => {
    const checkPolish = () => {
      // 文本润色按钮显隐：取决于「文本润色设定」里是否勾选了至少一个语言模型
      api.get('/api/ai/text-polish/models').then(data => {
        setTextPolishEnabled(Array.isArray(data?.models) && data.models.length > 0);
      }).catch(() => {});
    };
    checkPolish();
    window.addEventListener('modelsUpdated', checkPolish);
    return () => window.removeEventListener('modelsUpdated', checkPolish);
  }, []);

  // 🆕 文本格式状态（与 TextNode 一致）
  const [showColorPicker, setShowColorPicker] = useState(false);
  const fontSize = data.promptFontSize || 16;  // Prompt 默认字体大小
  const fontWeight = data.promptFontWeight || 'normal';
  const bgColor = data.promptBgColor || '#1e293b';
  
  const INDENT_CHAR = '\u3000\u3000';  // 全角空格缩进
  const text = data.prompt || '';
  const isIndented = text.split('\n').some(line => line.startsWith(INDENT_CHAR));

  // 🆕 格式工具函数（完整复制 TextNode）
  const updatePromptData = (updates) => handleUpdate({ ...updates });

  const toggleIndent = () => {
    const lines = text.split('\n');
    let newText;
    if (isIndented) {
      newText = lines.map(line => line.startsWith(INDENT_CHAR) ? line.replace(INDENT_CHAR, '') : line).join('\n');
    } else {
      newText = lines.map(line => (line.trim() && !line.startsWith(INDENT_CHAR)) ? `${INDENT_CHAR}${line}` : line).join('\n');
    }
    updatePromptData({ prompt: newText });
  };

  const setPreset = (type) => {
    switch(type) {
      case 'h1': updatePromptData({ promptFontSize: 24, promptFontWeight: 'bold' }); break;
      case 'h2': updatePromptData({ promptFontSize: 20, promptFontWeight: 'bold' }); break;
      case 'h3': updatePromptData({ promptFontSize: 16, promptFontWeight: 'bold' }); break;
      case 'p':  updatePromptData({ promptFontSize: 12, promptFontWeight: 'normal' }); break;
      default: break;
    }
  };

  const insertDivider = () => {
    const prefix = text.endsWith('\n') ? '' : '\n';
    const newText = text + prefix + '---\n';
    updatePromptData({ prompt: newText });
  };

  const insertNumbering = () => {
    const lines = text.split('\n');
    const lastLine = lines[lines.length - 1].trim();
    const match = lastLine.match(/^(\d+)\./); 
    let nextNum = 1;
    if (match) nextNum = parseInt(match[1]) + 1;
    const prefix = text.endsWith('\n') ? '' : '\n';
    const newText = text + prefix + `${nextNum}. `;
    updatePromptData({ prompt: newText });
  };

  const copyText = (e) => {
    e.stopPropagation();
    if (text) copyTextToClipboard(text);
  };

  const updateBgColor = (c) => { 
    updatePromptData({ promptBgColor: c }); 
    setShowColorPicker(false); 
  };

  // 工具栏 props
  const toolbarProps = {
    fontSize, 
    fontWeight, 
    isIndented, 
    showColorPicker, 
    bgColor,
    toggleColorPicker: () => setShowColorPicker(!showColorPicker),
    updateBgColor,
    setPreset, 
    toggleIndent, 
    insertDivider, 
    insertNumbering, 
    copyText
  };

  // 🆕 监听当前节点拖拽状态，拖拽时关闭下拉框
  const isDragging = useStore(s => s.nodeInternals.get(id)?.dragging);
  useEffect(() => {
    if (isDragging) setActiveDropdown(null);
  }, [isDragging]);

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleGlobalClick = () => setActiveDropdown(null);
    window.addEventListener('mousedown', handleGlobalClick);
    return () => window.removeEventListener('mousedown', handleGlobalClick);
  }, []);

  const loadModels = useCallback(async (force = false) => {
    setLoadingModels(true);
    setLoadModelsError('');
    try {
      const list = await getModelsForType(data.type, { force });

      // 🛡️ 空数组保护：API 返回 200 但无可用模型时，视为错误并允许重试
      if (!list || list.length === 0) {
        setModels([]);
        setLoadModelsError('暂无可用模型，点击重试');
        return;
      }

      // 模型顺序仅由服务端返回决定（映射优先级），避免不同设备本地顺序不一致。
      setModels(list);
    } catch (e) {
      console.warn('load models failed', e);
      setLoadModelsError(e?.message || '模型加载失败');
    } finally {
      setLoadingModels(false);
    }
  }, [data.type]);

  // 模型加载：依赖 data.type 而非 loadModels 函数引用，避免重复触发
  const loadModelsRef = useRef(loadModels);
  loadModelsRef.current = loadModels;

  useEffect(() => {
    let mounted = true;
    const guardedLoad = async (force = false) => {
      if (!mounted) return;
      await loadModelsRef.current(force);
    };

    guardedLoad(false);
    const onUpdate = () => guardedLoad(true);
    window.addEventListener('modelsUpdated', onUpdate);
    return () => { mounted = false; window.removeEventListener('modelsUpdated', onUpdate); };
  }, [data.type]); // ✅ 只在 type 变化时重新加载，不跟踪 loadModels 函数引用

  // 🧠 记住上次使用的模型 + 参数。普通节点按 data.type 存，分镜大师用独立槽（storyboard），
  //   互不污染。回填在新建节点时（EditorPage.buildNodeData）同步完成，这里只负责写入。
  useEffect(() => {
    if (!data.modelId) return;
    const memType = data.storyboardMode ? 'storyboard' : data.type;
    writeNodeDefaults(memType, {
      modelId: data.modelId,
      modelName: data.modelName,
      modelLogo: data.modelLogo,
      params: data.params || {},
    });
  }, [data.type, data.storyboardMode, data.modelId, data.modelName, data.modelLogo, data.params]);

  const imageFiles = data.refFiles?.filter(f => f.type === 'image') || [];
  const videoFiles = data.refFiles?.filter(f => f.type === 'video') || [];
  const audioFiles = data.refAudios || [];
  // 控制台灰字：标题截 5 字、描述词截 3 字，保持这行字的紧凑观感（发给模型的是全文）
  // descEnabled === false 的图不显示描述词，和提示词侧保持一致
  //
  // ⚠️ 这行条目之间用半角 ; 而不是全角 ；—— semanticRow 是 monospace，
  //    等宽字体下全角标点占满一个 CJK 字格（≈2 个半角位），字形本身却很小，
  //    左右就空出大片。
  //    标题引号用「」与提示词侧保持一致（那边换「」是为了躲开 JSON 转义成 \"）。
  //    标题与描述词之间**不加冒号**：「」的闭合括号本身就是边界，冒号纯属多余。
  //    ⚠️ 发给模型的那份仍保留冒号（见 useNodeActions.js）—— 那里冒号不是排版而是
  //    语义标记，明确告诉模型「后面这段是这张图的描述」，省一个字符换歧义不值得。
  const imgExplanations = imageFiles.map((f, i) => {
    const head = `图${i + 1}是「${formatShortTitle(f.title)}」`;
    const d = f.descEnabled === false ? '' : formatShortDesc(f.description);
    return d ? `${head}${d}` : head;
  });
  // 🆕 参考视频的描述词也显示（与图片同规则：标题 5 字 / 描述词 3 字、尊重 descEnabled）
  const vidExplanations = videoFiles.map((f, i) => {
    const head = `视频${i + 1}是「${formatShortTitle(f.title)}」`;
    const d = f.descEnabled === false ? '' : formatShortDesc(f.description);
    return d ? `${head}${d}` : head;
  });
  // 🆕 参考音频的描述词也显示（与图片/视频同规则：标题 5 字 / 描述词 3 字、尊重 descEnabled）
  const audioExplanations = audioFiles.map((a, i) => {
    const head = `音频${i + 1}是「${formatShortTitle(a.title)}」`;
    const d = a.descEnabled === false ? '' : formatShortDesc(a.description);
    return d ? `${head}${d}` : head;
  });
  const autoExplanation = [...imgExplanations, ...vidExplanations, ...audioExplanations].join('; ');

  // 🆕 内联气泡渲染：解析 prompt，将 @资产名 替换成带缩略图的气泡
  const renderPromptWithChips = useCallback((text) => {
    if (!text) return null;

    const parts = [];
    let lastIndex = 0;
    const matches = [];

    // 收集所有资产标题
    const allAssets = [
      ...imageFiles.map((f, i) => ({
        title: f.title,
        type: 'image',
        index: i + 1,
        mini_thumbnail_url: f.mini_thumbnail_url,
        thumbUrl: f.thumbUrl,
        url: f.url
      })),
      ...videoFiles.map((f, i) => ({
        title: f.title,
        type: 'video',
        index: i + 1,
        mini_thumbnail_url: f.mini_thumbnail_url,
        thumbUrl: f.thumbUrl,
        url: f.url
      })),
      ...audioFiles.map((f, i) => ({
        title: f.title,
        type: 'audio',
        index: i + 1,
        category: f.category,
        thumbUrl: null
      })),
    ];

    // 按标题长度降序排列，避免短标题先匹配导致长标题被截断
    // 例如：有"中2"和"中2号"两个资产，应该先匹配"中2号"
    allAssets.sort((a, b) => b.title.length - a.title.length);

    // 从左到右扫描文本，精确匹配 @资产名 或 @「资产名」
    let pos = 0;
    while (pos < text.length) {
      // 找到下一个 @
      const atPos = text.indexOf('@', pos);
      if (atPos === -1) break;

      // 尝试匹配所有资产标题（按长度降序，优先匹配长的）
      let matched = null;
      for (const asset of allAssets) {
        const afterAt = atPos + 1;

        // 🆕 先尝试匹配 @「资产名」格式
        if (text[afterAt] === '「' &&
            text.slice(afterAt + 1, afterAt + 1 + asset.title.length) === asset.title &&
            text[afterAt + 1 + asset.title.length] === '」') {
          matched = {
            index: atPos,
            length: 1 + 1 + asset.title.length + 1, // @ + 「 + 标题 + 」
            title: asset.title,
            type: asset.type,
            chipIndex: asset.index,
            thumbUrl: asset.thumbUrl,
          };
          break;
        }

        // 再尝试匹配 @资产名 格式（向后兼容）
        if (text.slice(afterAt, afterAt + asset.title.length) === asset.title) {
          matched = {
            index: atPos,
            length: 1 + asset.title.length, // @ + 标题长度
            title: asset.title,
            type: asset.type,
            chipIndex: asset.index,
            thumbUrl: asset.thumbUrl,
          };
          break;
        }
      }

      if (matched) {
        matches.push(matched);
        pos = matched.index + matched.length;
      } else {
        // 没匹配到，跳过这个 @
        pos = atPos + 1;
      }
    }

    // 按匹配位置拆分文本
    matches.forEach((m, idx) => {
      // 添加匹配之前的纯文本
      if (m.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, m.index) });
      }

      // 添加气泡
      parts.push({
        type: 'chip',
        assetType: m.type,
        index: m.chipIndex,
        thumbUrl: m.thumbUrl,
        title: m.title,
      });

      lastIndex = m.index + m.length;
    });

    // 添加剩余文本
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }

    return parts.length > 0 ? parts : null;
  }, [imageFiles, videoFiles, audioFiles]);

  // 🆕 素材构成签名：图片数 / 有无参考视频 / 有无参考音频
  // 用于检测「上游节点增删」导致的素材变化，驱动生成模式重算（见下方 useEffect）
  const assetSignature = `${imageFiles.length}-${videoFiles.length > 0 ? 'v' : ''}-${audioFiles.length > 0 ? 'a' : ''}`;

  // 新增：输入保护 Hook
  const promptRef = useRef(null);
  const consolePanelRef = useRef(null); // 🎬 分镜放大窗口动态匹配控制台尺寸用
  const [isPromptFocused, setIsPromptFocused] = useState(false);
  useInputProtection(promptRef, isPromptFocused);

  // 本地编辑缓冲：避免每次按键触发 setNodes 导致光标跳到末尾
  const [localPrompt, setLocalPrompt] = useState(data.prompt || '');
  const [localSlateValue, setLocalSlateValue] = useState(data.slateValue || null);  // 🔑 新增：保存 Slate 富文本结构

  // 当外部 data.prompt 变化且不在编辑状态时同步本地缓冲
  const prevDataPromptRef = useRef(data.prompt);
  const prevDataSlateValueRef = useRef(data.slateValue);

  useEffect(() => {
    // 🔑 只有在真正从外部改变（比如切换节点、加载项目）且失焦时才同步
    // 避免失焦后瞬间重置为空字符串的问题
    if (!isPromptFocused && data.prompt !== prevDataPromptRef.current) {
      console.log('[NodeConsole] 设置 localPrompt:', data.prompt?.substring(0, 50));
      setLocalPrompt(data.prompt || '');
    }
    if (!isPromptFocused && data.slateValue !== prevDataSlateValueRef.current) {
      console.log('[NodeConsole] 设置 localSlateValue:', data.slateValue?.substring(0, 100));
      setLocalSlateValue(data.slateValue || null);
    }
    // 每次 useEffect 执行后更新 ref（无论是否同步了 localPrompt）
    prevDataPromptRef.current = data.prompt;
    prevDataSlateValueRef.current = data.slateValue;
  }, [data.prompt, data.slateValue, isPromptFocused]);

  // ── @ / 联想功能 ────────────────────────────────────────────
  const [commandState, setCommandState] = useState(null); // { type: 'mention' | 'slash', query: string }
  const isComposingRef = useRef(false); // 跟踪 IME 组合输入状态

  // 防抖同步到 React Flow 节点状态（减少全量节点遍历次数）
  const debounceTimerRef = useRef(null);
  const mentionTimerRef = useRef(null); // 🛡️ 失焦时延迟关闭联想下拉的定时器
  const localPromptRef = useRef(localPrompt); // 🔑 保存最新的 localPrompt 供 cleanup 使用
  const localSlateValueRef = useRef(localSlateValue); // 🔑 保存最新的 localSlateValue 供 cleanup 使用
  const dataPromptRef = useRef(data.prompt); // 🔑 保存最新的 data.prompt 供对比
  const dataSlateValueRef = useRef(data.slateValue); // 🔑 保存最新的 data.slateValue 供对比
  const handleUpdateRef = useRef(handleUpdate); // 🔑 保存最新的 handleUpdate

  // 同步最新值到 ref
  useEffect(() => {
    localPromptRef.current = localPrompt;
    localSlateValueRef.current = localSlateValue;
    dataPromptRef.current = data.prompt;
    dataSlateValueRef.current = data.slateValue;
    handleUpdateRef.current = handleUpdate;
  }, [localPrompt, localSlateValue, data.prompt, data.slateValue, handleUpdate]);

  const debouncedHandleUpdate = useCallback((updates) => {
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      handleUpdate(updates);
    }, 300);
  }, [handleUpdate]);

  // 组件卸载时清理定时器，并保存当前提示词（防止切换节点时丢失未保存的内容）
  useEffect(() => () => {
    clearTimeout(debounceTimerRef.current);
    clearTimeout(mentionTimerRef.current);
    // 🔑 关键修复：组件销毁前强制保存当前的 localPrompt 和 localSlateValue
    // 使用 ref 获取最新值，避免闭包陷阱
    if (localPromptRef.current !== dataPromptRef.current || localSlateValueRef.current !== dataSlateValueRef.current) {
      handleUpdateRef.current({
        prompt: localPromptRef.current,
        slateValue: localSlateValueRef.current
      });
    }
  }, []); // 🔑 空依赖数组，只在真正卸载时执行

  // 🔑 新增：将 Slate 富文本光标位置转换为纯文本偏移量
  const getSlateTextOffset = useCallback((editor) => {
    try {
      const { selection } = editor;
      if (!selection) return null;

      let offset = 0;
      const anchorPath = selection.anchor.path;
      const anchorOffset = selection.anchor.offset;

      // 遍历 editor.children（paragraph 节点数组）
      for (let pIdx = 0; pIdx < editor.children.length; pIdx++) {
        const paragraph = editor.children[pIdx];

        // 检查光标是否在当前 paragraph
        if (pIdx < anchorPath[0]) {
          // 光标在后面的 paragraph，累加整个 paragraph 的长度
          for (const child of paragraph.children) {
            if (Text.isText(child)) {
              offset += child.text.length;
            } else if (child.type === 'mention') {
              // mention 气泡对应的纯文本是 @「标题」
              const title = child.title || '';
              offset += `@「${title}」`.length; // 完整长度：@ + 「 + 标题 + 」
            }
          }
          // paragraph 之间的换行符
          offset += 1;
        } else if (pIdx === anchorPath[0]) {
          // 光标在当前 paragraph 内部
          for (let cIdx = 0; cIdx < paragraph.children.length; cIdx++) {
            const child = paragraph.children[cIdx];

            if (cIdx < anchorPath[1]) {
              // 光标在后面的 child
              if (Text.isText(child)) {
                offset += child.text.length;
              } else if (child.type === 'mention') {
                const title = child.title || '';
                offset += `@「${title}」`.length; // 完整长度：@ + 「 + 标题 + 」
              }
            } else if (cIdx === anchorPath[1]) {
              // 光标在当前 child 内部
              offset += anchorOffset;
              break;
            }
          }
          break;
        }
      }

      return offset;
    } catch (err) {
      console.warn('[getSlateTextOffset] 计算失败:', err);
      return null;
    }
  }, []);

  // 从光标位置向前扫描最近的 @xxx 或 /xxx 或 #xxx
  // 🔑 命令检测：移到 utils/mentionUtils.js，这里直接用（控制台支持 @ 和 /）

  const handlePromptChange = useCallback((e) => {
    const val = e.target.value;
    const slateVal = e.target.slateValue;  // 🔑 获取 Slate 富文本结构

    // 先更新本地缓冲
    setLocalPrompt(val);
    setLocalSlateValue(slateVal);  // 🔑 同时保存富文本结构

    // 防抖同步到父级 React Flow 节点状态（300ms 内多次按键只触发一次遍历）
    debouncedHandleUpdate({
      prompt: val,
      slateValue: slateVal  // 🔑 同时传递富文本结构
    });

    // 立即检测 @ / 触发下拉（不等 keyUp）
    // 🔑 使用 Slate 编辑器获取真实命令状态
    const editor = promptRef.current?.editor;
    const nextCommand = editor ? getSlateCommandState(editor, ['@', '/']) : null;
    setCommandState(nextCommand ? { type: nextCommand.trigger, query: nextCommand.query } : null);
  }, [debouncedHandleUpdate]);

  // Slate 编辑器的键盘事件处理：检测 @ / 触发下拉
  const handleSlateKeyUp = useCallback((e) => {
    // IME 组合期间不检测触发符（等 compositionEnd 后处理）
    if (isComposingRef.current) return;

    // 🔑 使用 Slate 编辑器获取真实命令状态
    const editor = promptRef.current?.editor;
    const nextCommand = editor ? getSlateCommandState(editor, ['@', '/']) : null;
    setCommandState(nextCommand ? { type: nextCommand.trigger, query: nextCommand.query } : null);
  }, []);

  // IME 组合结束：用 setTimeout(0) 推迟执行，确保在 onChange 之后覆盖结果
  const handleCompositionEnd = useCallback((e) => {
    isComposingRef.current = false;
    // 🔑 使用 Slate 编辑器获取真实命令状态
    const editor = promptRef.current?.editor;
    const nextCommand = editor ? getSlateCommandState(editor, ['@', '/']) : null;
    // 延迟到本轮事件循环末尾，覆盖 onChange 可能设的 null
    setTimeout(() => {
      setCommandState(nextCommand ? { type: nextCommand.trigger, query: nextCommand.query } : null);
    }, 0);
  }, [getCommandState]);

  const handleMentionSelect = useCallback((asset) => {
    // 使用 Slate 编辑器的 insertMention 方法插入气泡
    if (promptRef.current?.insertMention) {
      promptRef.current.insertMention(asset);
    }

    // 🆕 立即触发 onMentionSync，创建上游节点（不等 useEffect）
    // 因为 handlePromptChange 有 300ms 防抖，会导致 data.prompt 延迟更新
    if (data.onMentionSync) {
      // 🔑 记录添加前的素材列表，用于判断是否为新增素材
      const beforeRefFiles = data.refFiles || [];
      const beforeIds = new Set(beforeRefFiles.map(f => f.id));

      // 获取当前已有的 @ 提及资产
      const currentPrompt = localPrompt || data.prompt || '';
      const assets = data.globalAssetsRef?.current || [];

      // 计算新的 @ 提及列表（包含刚选择的资产）
      // 🔑 使用新格式 @「标题」
      const newPrompt = currentPrompt + `@「${asset.title}」`;
      const matched = assets.filter(a => a.title && newPrompt.includes(`@「${a.title}」`));
      matched.sort((a, b) => {
        const ia = newPrompt.indexOf(`@「${a.title}」`);
        const ib = newPrompt.indexOf(`@「${b.title}」`);
        return ia - ib;
      });

      // 🎯 智能过滤：只为"新增素材"创建上游节点
      // - 如果素材已经在 refFiles 中（继承自分镜大师），则跳过弹出节点
      // - 如果是用户新添加的素材，则正常弹出节点
      const newAssets = matched.filter(a => !beforeIds.has(a.id));

      // 立即调用 onMentionSync，只为新增素材创建上游节点和连线
      data.onMentionSync(newAssets, id);
    }

    setCommandState(null);
    setTimeout(() => promptRef.current?.focus(), 0);
  }, [data, id, localPrompt]);

  const handleSlashSelect = useCallback((asset) => {
    if (!asset || asset.type !== 'text') return;

    const currentPrompt = localPrompt || '';
    const cursor = promptRef.current?.selectionStart ?? currentPrompt.length;
    const before = currentPrompt.slice(0, cursor);
    const after = currentPrompt.slice(cursor);
    const replaced = before.replace(/\/([^/\s@]*)$/, '');
    const newPrompt = replaced + after;

    clearTimeout(debounceTimerRef.current);
    setLocalPrompt(newPrompt);
    handleUpdate({ prompt: newPrompt });
    setCommandState(null);
    data.onSlashAssetSpawn?.(asset, id);
    setTimeout(() => promptRef.current?.focus(), 0);
  }, [data, handleUpdate, id, localPrompt]);

  const activeCommandType = commandState?.type || null;
  const dropdownTitle = activeCommandType === 'slash' ? '咒语匹配'
    : activeCommandType === 'lora' ? 'LoRA 模型'
    : '素材库匹配';
  const dropdownFilter = activeCommandType === 'slash'
    ? (asset) => asset?.type === 'text'
    : activeCommandType === 'lora'
    ? (asset) => asset?.type === 'lora'
    : (asset) => asset?.type !== 'text';

  // @ 菜单的素材列表 —— 只显示"可作为参考素材"的内容
  //   排除项：
  //     1. type === 'text'   → 文本咒语，归到 / 菜单（slashAssets），不在 @ 里
  //     2. category === 'default' → 工作流（Workflow），是节点拓扑模板，
  //        @ 提到没意义，且其 url/thumbnail 通常是占位图
  //   保留：图片/视频/音频素材（角色、场景、道具、音乐、音效、音色等）
  // 🔧 只显示项目素材库，不显示剧组库（避免误选剧组库资产导致权限问题）
  const mentionAssets = [
    ...(data.globalAssetsRef?.current || []),
  ].filter(
    a => a?.type !== 'text' && a?.category !== 'default'
  );
  // 🆕 提示词编辑器（控制台输入框 + 全屏编辑器）自动转气泡用：素材库优先，其次本节点参考素材
  const promptMentionAssets = [...mentionAssets, ...(data.refFiles || []), ...(data.refAudios || [])];
  // ⚠️ 依赖必须是 .current（数组本身），不能是 ref 对象 ——
  //    ref 对象 identity 永远不变，useMemo 永远不会重算，
  //    导致咒语删除后斜杠下拉里仍残留旧条目。
  const projectAssets = data.globalAssetsRef?.current || [];
  const crewLibraryAssets = data.publicAssetsRef?.current || [];
  const globalSpellAssets = data.globalSpellsRef?.current || [];
  const slashAssets = React.useMemo(() => {
    const merged = [...globalSpellAssets, ...crewLibraryAssets, ...projectAssets].filter(a => a?.type === 'text');
    const deduped = [];
    const seen = new Set();

    const normalizeText = (asset) => String(asset?.text || asset?.data?.text || asset?.url || '').trim();
    for (const asset of merged) {
      const textKey = normalizeText(asset);
      const key = textKey ? `text:${textKey}` : `title:${asset?.title || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(asset);
    }
    return deduped;
  }, [globalSpellAssets, crewLibraryAssets, projectAssets]);

  // ── 文本驱动：监听 prompt 中的 @name，自动同步参考图 + 左侧节点 ──
  const prevMentionKeyRef = useRef('');
  useEffect(() => {
    if (data.storyboardMode) return; // 🆕 分镜节点走填充联想，不走 @ 提及，避免隐藏 console 误删节点
    if (isComposingRef.current) return;
    const prompt = data.prompt || '';
    const assets = data.globalAssetsRef?.current || [];
    // 素材库尚未加载且之前也没有挂载过提及节点，无需处理
    if (!assets.length && !prevMentionKeyRef.current) return;

    // 从文本中找出所有 @「title」 匹配的素材
    const matched = assets.filter(a => a.title && prompt.includes(`@「${a.title}」`));
    matched.sort((a, b) => {
      const ia = prompt.indexOf(`@「${a.title}」`);
      const ib = prompt.indexOf(`@「${b.title}」`);
      return ia - ib;
    });

    // 🎬 过滤掉已经通过连线引用的资产（如分镜大师继承来的参考图）
    //   连线引用的 refFiles 的 id 是节点ID（不以 mention- 开头）
    //   @ 提及创建的 refFiles 的 id 是 mention-${assetId}-for-${targetNodeId}
    //   只过滤那些通过连线引用的（标题匹配且 id 不以 mention- 开头）
    const connectedRefTitles = new Set(
      (data.refFiles || [])
        .filter(f => !String(f.id).startsWith('mention-'))
        .map(f => f.title)
    );
    const needMentionNode = matched.filter(a => !connectedRefTitles.has(a.title));

    // 用有序 ID 串作为稳定 key，避免对象引用变化造成重复触发
    const key = needMentionNode.map(a => a.id).sort().join(',');

    // 集合未变化（同一批素材）则跳过，防止每次渲染都触发回调
    if (key === prevMentionKeyRef.current) return;
    prevMentionKeyRef.current = key;

    // 通知 EditorPage 统一管理：参考图 + 节点 + 连线
    data.onMentionSync?.(needMentionNode, id);
  }, [data.prompt]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Dynamic Params Logic ---
  // Ensure we compare IDs loosely (string vs number) just in case
  const currentModel = models.find(m => String(m.id) === String(data.modelId));
  const isPromptUnsupportedForCurrentModel = isImageBlenderModel(currentModel);
  const isAspectRatioSuppressedForCurrentModel = isAspectRatioSuppressedModel(currentModel);
  const promptUnsupportedText = '融创，本模式不支持提示词输入';

  // 🎨 预设工具箱：预设列表 + 当前节点选中的预设
  //   - 仅图片生成节点显示预设下拉（生成次数前，按分组展示当前工具集内预设）
  //   - 选中预设后用其 description 替换输入框灰色 placeholder（除非被开关关闭）
  //   - 生图时由 handleGenerate 按 presetId 实时取 prompt 注入（presetDisabled 时不注入）
  const presetTree = (data.getPresets?.() || { toolsets: [] });
  const isImageGenNode = (data.type === 'image') && !data.isDirectUpload && !data.assetId && !data.isDetached;
  const presetHit = data.presetId != null ? findPreset(presetTree, data.presetId) : null;
  const selectedPreset = presetHit?.preset || null;
  const currentToolset = data.presetId != null ? findToolsetByPresetId(presetTree, data.presetId) : null;
  const presetDisabled = !!data.presetDisabled;
  // 仅在「选中预设且未被关闭」时，用描述词替换 placeholder
  const presetPlaceholder = (selectedPreset && !presetDisabled) ? selectedPreset.description?.trim() : null;

  // ✨ 技能工具箱三层结构：与预设工具箱平行逻辑
  //   - 图片生成节点显示技能下拉（在预设下拉后面，按分组展示当前工具集内技能）
  //   - 选中技能后用其 description 替换输入框灰色 placeholder（除非被开关关闭）
  //   - 生图时由 handleGenerate 按 skillboxPresetId 实时取 prompt 注入（skillboxDisabled 时不注入）
  const skillboxTree = (data.getSkillbox?.() || { toolsets: [] });
  const skillboxHit = data.skillboxPresetId != null ? findSkillboxPreset(skillboxTree, data.skillboxPresetId) : null;
  const selectedSkillboxPreset = skillboxHit?.preset || null;
  const currentSkillboxToolset = skillboxHit?.toolset || null;
  const skillboxDisabled = !!data.skillboxDisabled;
  // 技能的 placeholder 与预设合并：都有时用换行拼接
  const skillboxPlaceholder = (selectedSkillboxPreset && !skillboxDisabled) ? selectedSkillboxPreset.description?.trim() : null;

  // 🎨 预设限定模型：预设在配置面板勾选了可用模型时，本节点模型下拉只显示这些模型。
  //   modelIds 为空 = 不限制（存量预设默认行为）；预设开关关闭时也解除限制。
  //   交集为空（模型被下架/禁用）时不做限制，避免下拉整个空掉没法选。
  const presetModelIds = (selectedPreset && !presetDisabled && Array.isArray(selectedPreset.modelIds))
    ? selectedPreset.modelIds : [];
  const visibleModels = useMemo(() => {
    if (presetModelIds.length === 0) return models;
    const allow = new Set(presetModelIds.map(String));
    const hit = models.filter(m => allow.has(String(m.id)));
    return hit.length > 0 ? hit : models;
  }, [models, presetModelIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // # 菜单的 LoRA 列表 —— 从当前模型的 parameters.lora.loras 读取
  const loraAssets = React.useMemo(() => {
    const loras = currentModel?.parameters?.lora?.loras || [];
    // 转换为 MentionDropdown 可识别的格式
    return loras.map((lora, idx) => ({
      id: `lora-${idx}`,
      title: lora.label || lora.name,
      type: 'lora',
      category: 'lora',
      thumbnailUrl: lora.thumbnail || null,
      loraName: lora.name, // 存储模型文件名
    }));
  }, [currentModel]);

  // 🆕 参考图列表 —— 从当前技能的 referenceImages 读取
  const refImageAssets = React.useMemo(() => {
    if (!selectedSkillboxPreset || !selectedSkillboxPreset.referenceEnabled) {
      return [];
    }

    const images = selectedSkillboxPreset.referenceImages || [];
    // 转换为 MentionDropdown 可识别的格式
    return images.map((img) => ({
      id: img.id,
      title: img.name,
      type: 'refImage',
      category: 'refImage',
      thumbnailUrl: img.thumbnailUrl,
      fullUrl: img.fullUrl,
      prompt: img.prompt || '',
    }));
  }, [selectedSkillboxPreset]);

  // 🆕 获取当前选中的参考图
  const selectedRefImage = React.useMemo(() => {
    if (!data.skillboxRefImageId) return null;
    return refImageAssets.find(img => img.id === data.skillboxRefImageId);
  }, [data.skillboxRefImageId, refImageAssets]);

  // 🆕 获取参考图标签（风格/场景/氛围等，可配置）
  const refImageLabel = selectedSkillboxPreset?.referenceLabel || '风格';

  // ── LoRA 强度管理 ──────────────────────────────────────────
  // LoRA 强度存储在 data.params.loraStrengths: { "LoRA名称": 强度值 }
  // 用 useMemo 稳定化引用，避免每次渲染都创建新对象导致依赖它的 useMemo 无限触发
  // 🔧 用 JSON 序列化稳定依赖，避免对象引用变化导致无效重计算
  const loraStrengthsKey = JSON.stringify(data.params?.loraStrengths || {});
  const loraStrengths = React.useMemo(() => {
    return data.params?.loraStrengths || {};
  }, [loraStrengthsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoraSelect = useCallback((asset) => {
    if (!asset || asset.type !== 'lora') return;

    // 单选模式：替换为新选择的 LoRA（只保留一个）
    const newStrengths = {
      [asset.loraName]: {
        strength: 1,
        label: asset.title
      }
    };
    console.log('[LoRA Select] asset:', asset);
    console.log('[LoRA Select] newStrengths:', newStrengths);
    handleUpdate({
      params: { ...data.params, loraStrengths: newStrengths }
    });
  }, [data.params, handleUpdate]);

  // 从 prompt 中提取所有 #标签#
  const extractLoraTags = useCallback((text) => {
    if (!text) return [];
    const regex = /#([^#\s]+)#/g;
    const matches = [];
    let match;
    while ((match = regex.exec(text)) !== null) {
      matches.push(match[1]); // LoRA 名称（label）
    }
    return [...new Set(matches)]; // 去重
  }, []);

  // 删除 LoRA（从 loraStrengths 中移除）
  const removeLoraTag = useCallback((loraName) => {
    const newStrengths = { ...loraStrengths };
    delete newStrengths[loraName];
    handleUpdate({
      params: { ...data.params, loraStrengths: newStrengths }
    });
  }, [data.params, loraStrengths, handleUpdate]);

  // 🆕 参考图选择处理
  const handleRefImageSelect = useCallback((asset) => {
    if (!asset || asset.type !== 'refImage') return;

    // 存储选中的参考图 ID
    handleUpdate({
      skillboxRefImageId: asset.id
    });

    console.log('[RefImage Select] asset:', asset);
    setRefImageListVisible(false);
  }, [handleUpdate]);

  // 🆕 删除参考图选择
  const removeRefImageTag = useCallback(() => {
    handleUpdate({
      skillboxRefImageId: null
    });
  }, [handleUpdate]);

  const setLoraStrength = useCallback((loraName, strength) => {
    const oldData = loraStrengths[loraName];
    const newStrengths = {
      ...loraStrengths,
      [loraName]: typeof oldData === 'object'
        ? { ...oldData, strength }
        : { strength, label: loraName } // 兼容旧格式
    };
    handleUpdate({
      params: { ...data.params, loraStrengths: newStrengths }
    });
  }, [data.params, loraStrengths, handleUpdate]);

  // 当前已选择的 LoRA 列表（用于上方显示方块）
  const activeLoraTags = React.useMemo(() => {
    // 从 loraStrengths 读取已选择的 LoRA（新格式：{ loraName: { strength, label } }）
    const loraNames = Object.keys(loraStrengths);
    const result = loraNames.map(loraName => {
      const loraData = loraStrengths[loraName];
      // 兼容旧格式（直接存强度值）和新格式（存对象）
      const strength = typeof loraData === 'number' ? loraData : loraData.strength;
      const label = typeof loraData === 'object' ? loraData.label : loraName;

      // 从模型配置查找缩略图
      const loraConfig = currentModel?.parameters?.lora?.loras?.find(
        l => l.name === loraName
      );

      return {
        name: loraName, // 文件名
        label: label,   // 显示名称
        thumbnail: loraConfig?.thumbnail || null,
        strength: strength ?? 1.0,
      };
    });
    return result;
  }, [currentModel, loraStrengths]);

  // 可用的 LoRA 列表（从模型配置读取）
  const availableLoras = React.useMemo(() => {
    const loras = currentModel?.parameters?.lora?.loras || [];
    return loras.map(lora => ({
      name: lora.name,
      label: lora.label || lora.name,
      thumbnail: lora.thumbnail || null,
    }));
  }, [currentModel]);

  // 添加 LoRA 标签
  const addLoraTag = useCallback((lora) => {
    const newStrengths = {
      ...loraStrengths,
      [lora.name]: {
        strength: 1.0,
        label: lora.label
      }
    };
    handleUpdate({
      params: { ...data.params, loraStrengths: newStrengths }
    });
  }, [data.params, loraStrengths, handleUpdate]);

  // 🆕 当前已选择的参考图（用于上方显示方块）
  const activeRefImageTag = React.useMemo(() => {
    if (!data.skillboxRefImageId || !selectedSkillboxPreset || !selectedSkillboxPreset.referenceEnabled) {
      return null;
    }
    const images = selectedSkillboxPreset.referenceImages || [];
    const selectedImage = images.find(img => img.id === data.skillboxRefImageId);
    if (!selectedImage) {
      return null;
    }
    return {
      id: selectedImage.id,
      name: selectedImage.name,
      thumbnailUrl: selectedImage.thumbnailUrl,
      fullUrl: selectedImage.fullUrl,
      prompt: selectedImage.prompt || '',
    };
  }, [data.skillboxRefImageId, selectedSkillboxPreset]);

  // LoRA 强度调节器状态（点击方块时弹出）
  const [activeLoraSlider, setActiveLoraSlider] = useState(null);

  // LoRA 列表弹窗状态（点击 LoRA 按钮时弹出）
  const [loraListVisible, setLoraListVisible] = useState(false);
  const loraButtonRef = useRef(null);

  // 🆕 参考图列表弹窗状态（点击参考图按钮时弹出）
  const [refImageListVisible, setRefImageListVisible] = useState(false);
  const refImageButtonRef = useRef(null);

  // 点击空白处关闭 LoRA 滑杆
  useEffect(() => {
    if (!activeLoraSlider) return;
    const handleClickOutside = (e) => {
      // 点击滑杆内部不关闭，点击其他任何地方都关闭
      const target = e.target;
      const isSlider = target.closest('[data-lora-slider]');
      if (!isSlider) {
        setActiveLoraSlider(null);
      }
    };
    // 使用捕获阶段，确保先于其他事件处理
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [activeLoraSlider]);

  // 点击空白处关闭 LoRA 列表弹窗
  useEffect(() => {
    if (!loraListVisible) return;
    const handleClickOutside = (e) => {
      const target = e.target;
      const isLoraList = target.closest('[data-lora-list-popup]');
      const isLoraButton = target.closest('[data-lora-button]');
      if (!isLoraList && !isLoraButton) {
        setLoraListVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [loraListVisible]);

  // 🆕 点击空白处关闭参考图列表弹窗
  useEffect(() => {
    if (!refImageListVisible) return;
    const handleClickOutside = (e) => {
      const target = e.target;
      const isRefImageList = target.closest('[data-refimage-list-popup]');
      const isRefImageButton = target.closest('[data-refimage-button]');
      if (!isRefImageList && !isRefImageButton) {
        setRefImageListVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [refImageListVisible]);

  // 🆕 自动补全 modelName 和 modelLogo (如果 data 中缺失)
  useEffect(() => {
    if (data.modelId && currentModel) {
      const newName = (!data.modelName && currentModel.name) ? currentModel.name : null;
      const newLogo = (!data.modelLogo && currentModel.logo_url) ? currentModel.logo_url : null;
      // 只在确实有新值可写入时才更新，避免 logo_url 为 null 时触发无限循环
      if (newName || newLogo) {
        handleUpdate({ 
          ...(newName ? { modelName: newName } : {}),
          ...(newLogo ? { modelLogo: newLogo } : {})
        });
      }
    }
  }, [data.modelId, data.modelName, data.modelLogo, currentModel, handleUpdate]);

  // Default Fallbacks (for legacy models without config)
  const ALL_RATIOS = ['1:1','3:4','4:3','16:9','9:16','2:3','3:2','4:5','5:4','21:9'];

  // Start with null to indicate "not found in config"
  let configRatios = null;
  let configSizes = null;
  // 模型身份串：extra_json.model_id + 模型名 + 厂商 id 拼一起，供下面几个
  // seedance 判断复用（后台改过名的行 model_id 才是真值，名字不一定带 seedance）
  const modelIdentity = (() => {
    if (!currentModel) return '';
    let mid = '';
    try {
      const ej = currentModel.extra_json;
      const extra = typeof ej === 'string' ? JSON.parse(ej) : (ej || {});
      mid = String(extra.model_id || '');
    } catch {}
    return [
      mid,
      currentModel.name || '',
      currentModel.vendor_id || currentModel.vendor || '',
    ].join(' ').toLowerCase();
  })();
  const isSeedanceModel = modelIdentity.includes('seedance');
  // Seedance 2.5：音频/水印不开放给用户选（产品决定，固定有声 + 无水印），
  // 所以生成面板不能出「有声/无声」下拉。
  // ⚠️ 必须按 2.5 单独判，不能只靠 enabled_parameters：
  //   ① 存量模型行的 enabled_parameters 里还留着历史同步进去的 generate_audio，
  //      后台点「同步模型」之前一直是脏数据；
  //   ② 下面还有一段「seedance 未配置就兜底给 true/false」的逻辑，
  //      清空配置反而会命中兜底，下拉照样冒出来。
  // 版本号写法在各处不统一（seedance-2.5 / seedance2.5 / seedance-2-5 / "Seedance 2.5"），
  // 所以要求 seedance 紧跟版本号，而不是在整串里裸搜 2.5
  // ——否则「Seedance 2.0（25帧）」这类名字会被误判成 2.5。
  // 命中：seedance-2.5 / seedance2.5 / seedance-2-5 / seedance 2.5
  // 不命中：doubao-seedance-2.0 / seedance-2-fast
  const isSeedance25Model = /seedance[^a-z0-9]*2[.\-_ ]?5/.test(modelIdentity);

  // Seedance 2.0 Mini：固定有声，不给用户选择
  const isSeedance20MiniModel = /seedance[^a-z0-9]*2[.\-_ ]?0[^a-z0-9]*mini/.test(modelIdentity);

  // Seedance 2.0 Fast：固定有声，不给用户选择
  const isSeedance20FastModel = /seedance[^a-z0-9]*2[.\-_ ]?0[^a-z0-9]*fast/.test(modelIdentity);

  // Seedance 2.0（不带 Mini/Fast 后缀）：固定有声，不给用户选择
  const isSeedance20Model = /seedance[^a-z0-9]*2[.\-_ ]?0(?![^a-z0-9]*(mini|fast))/.test(modelIdentity);

  // Seedance 2.5 首帧/首尾帧任务：上游只接受 size=adaptive（成片比例跟随首帧），
  // 选 16:9 会被同步 400 拒掉。而 size 默认就是 16:9，
  // 所以「拖一张参考图直接点生成」是必失败路径——用户没配错任何东西。
  // 这里把比例下拉收窄成只剩「原尺寸」（adaptive 的 UI 叫法），选不到注定被拒的值。
  // ⚠️ 判定必须跟着 server/providers/apimart-seedance.js 的 buildImagePayload 走：
  //   image_mode 为空时服务端按图片数兜底（1 张→首帧、2 张→首尾帧），
  //   只看 image_mode 会漏掉空值那条路径，照样发出 16:9。
  //   有音频/视频参考时服务端强制 multi_reference，不受此限制。
  const isSeedance25FrameTask = (() => {
    if (!isSeedance25Model) return false;
    if (audioFiles.length > 0 || videoFiles.length > 0) return false;
    const mode = data.params?.image_mode;
    if (mode === 'first_frame' || mode === 'first_last_frame') return true;
    if (!mode && (imageFiles.length === 1 || imageFiles.length === 2)) return true;
    return false;
  })();

  // 🆕 HappyHorse 判定（它的模式集与 Seedance/H3 完全不同，需单独分支）
  const isHappyHorseModel = (() => {
    if (!currentModel) return false;
    const hit = (s) => String(s || '').toLowerCase().includes('happyhorse');
    try {
      const ej = currentModel.extra_json;
      const extra = typeof ej === 'string' ? JSON.parse(ej) : (ej || {});
      if (hit(extra.model_id)) return true;
    } catch {}
    if (hit(currentModel.name)) return true;
    if (hit(currentModel.vendor_id || currentModel.vendor)) return true;
    return false;
  })();

  // 🆕 判断是否支持 image_mode 参数（Seedance、MiniMax-H3、HappyHorse 等多模态模型）
  const supportsImageMode = (() => {
    if (!currentModel) return false;
    // ① 首选：管理员在后台启用了 image_mode，说明该模型声明了这个参数。
    //    这样新接的多模态模型不用再回来改这里的名字白名单。
    try {
      const ep = currentModel.enabled_parameters;
      const parsed = typeof ep === 'string' ? JSON.parse(ep) : (ep || {});
      if (Array.isArray(parsed.image_mode) && parsed.image_mode.length > 0) return true;
    } catch {}
    // ② 兜底：老数据没配 enabled_parameters 时按模型名/厂商识别
    const KEYWORDS = ['seedance', 'happyhorse', 'minimax-h3'];
    const hit = (s) => KEYWORDS.some(k => String(s || '').toLowerCase().includes(k));
    try {
      const ej = currentModel.extra_json;
      const extra = typeof ej === 'string' ? JSON.parse(ej) : (ej || {});
      if (hit(extra.model_id)) return true;
    } catch {}
    if (hit(currentModel.name)) return true;
    if (hit(currentModel.vendor_id || currentModel.vendor)) return true;
    return false;
  })();

  // 🆕 视频节点参数
  let configVideoRatios = null;  // 视频比例 (aspectRatio)
  let configDurations = null;    // 视频时长 (duration)
  let configVideoSizes = null;   // 视频清晰度 (size)
  let configTemplates = null;    // 运镜模板 (template_id)
  let configCameraStrength = null; // 运镜强度 (camera_strength)
  let configImageModes = null;   // Seedance 图片模式 (image_mode)
  let configResolutions = null;  // 分辨率 (resolution)
  let configGenerateAudio = null; // Seedance 生成音频开关 (generate_audio)
  // 🆕 MJ 专用参数
  let configQuality = null;      // MJ 质量 (--q)
  let configVersion = null;      // MJ 版本 (--v)

  const normalizeVersionValue = (value) => {
    if (typeof value !== 'string') return value;
    const cleaned = value.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
    const compact = cleaned.replace(/\s+/g, '').toLowerCase();

    const nijiMatch = compact.match(/^--niji(\d+(?:\.\d+)?)$/);
    if (nijiMatch) return `-- niji ${nijiMatch[1]}`;

    const vMatch = compact.match(/^--v(\d+(?:\.\d+)?)$/);
    if (vMatch) return `--v ${vMatch[1]}`;

    return cleaned;
  };

  const dedupeWithNormalizer = (arr, normalizer = (v) => v) => {
    const out = [];
    const seen = new Set();
    (Array.isArray(arr) ? arr : []).forEach((item) => {
      const normalized = normalizer(item);
      const key = String(normalized);
      if (!seen.has(key)) {
        seen.add(key);
        out.push(normalized);
      }
    });
    return out;
  };
  const preferredVersionOrder = ['--v 8.2', '--v 8.1', '--v 8', '--v 7', '--v 6.1', '--v 6.0', '--v 5.2', '-- niji 8.1', '-- niji 8', '-- niji 7', '-- niji 6'];

  // 🔧 稳定化 parsedEnabledParams，避免每次渲染都创建新对象导致依赖它的变量无限循环
  // 使用 JSON 序列化作为依赖，避免对象引用变化导致无效重计算
  const enabledParamsKey = JSON.stringify(currentModel?.enabled_parameters || {});
  const parsedEnabledParams = React.useMemo(() => {
    let params = {};
    if (currentModel) {
      try {
        if (currentModel.enabled_parameters) {
          if (typeof currentModel.enabled_parameters === 'string') {
            params = JSON.parse(currentModel.enabled_parameters);
          } else if (typeof currentModel.enabled_parameters === 'object') {
            params = currentModel.enabled_parameters;
          }
        }
      } catch (e) {
        console.warn('parse enabled_parameters failed', e);
      }
    }
    return params;
  }, [currentModel?.id, enabledParamsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    if (currentModel) {

    // 从 enabled_parameters 获取配置的参数值
    if (Array.isArray(parsedEnabledParams.aspect_ratio) && parsedEnabledParams.aspect_ratio.length > 0) {
      configRatios = parsedEnabledParams.aspect_ratio;
    }
    if (Array.isArray(parsedEnabledParams.image_size) && parsedEnabledParams.image_size.length > 0) {
      configSizes = parsedEnabledParams.image_size;
    }
    // 🆕 视频参数：统一使用下划线风格 (aspect_ratio / duration / size)
    // 兼容 ratio 参数（部分模型如 Seedance 2.0 Mini 使用 ratio 而不是 aspect_ratio）
    if (Array.isArray(parsedEnabledParams.aspect_ratio) && parsedEnabledParams.aspect_ratio.length > 0) {
      configVideoRatios = parsedEnabledParams.aspect_ratio;
      // 首帧/首尾帧任务上游只收 adaptive，同下面 size 那一档的道理。
      // 新接的模型（如 RunningHub）比例走规范键 aspect_ratio 而不是历史的 size，
      // 这里不收窄的话下拉会停在 16:9、服务端却夹成 adaptive，显示与出片对不上。
      // 带 includes 守卫：存量模型的 aspect_ratio 里没有 adaptive，不受影响。
      if (isSeedance25FrameTask && configVideoRatios.includes('adaptive')) {
        configVideoRatios = ['adaptive'];
      }
    } else if (Array.isArray(parsedEnabledParams.ratio) && parsedEnabledParams.ratio.length > 0) {
      // 如果没有 aspect_ratio 但有 ratio，使用 ratio
      configVideoRatios = parsedEnabledParams.ratio;
    }
    if (Array.isArray(parsedEnabledParams.duration) && parsedEnabledParams.duration.length > 0) {
      configDurations = parsedEnabledParams.duration;
    }
    if (Array.isArray(parsedEnabledParams.size) && parsedEnabledParams.size.length > 0) {
      configVideoSizes = parsedEnabledParams.size;
      // Seedance 2.5 首帧/首尾帧：上游只收 adaptive，其他档位选了必被 400（详见 isSeedance25FrameTask）
      if (isSeedance25FrameTask && configVideoSizes.includes('adaptive')) {
        configVideoSizes = ['adaptive'];
      }
    }
    // 🆕 运镜专用参数
    if (Array.isArray(parsedEnabledParams.template_id) && parsedEnabledParams.template_id.length > 0) {
      configTemplates = parsedEnabledParams.template_id;
    }
    if (Array.isArray(parsedEnabledParams.camera_strength) && parsedEnabledParams.camera_strength.length > 0) {
      configCameraStrength = parsedEnabledParams.camera_strength;
    }
    // 多模态模型图片模式（Seedance、HappyHorse）
    if (Array.isArray(parsedEnabledParams.image_mode) && parsedEnabledParams.image_mode.length > 0) {
      configImageModes = parsedEnabledParams.image_mode;
    } else if (supportsImageMode) {
      // 兜底：未配置时根据模型类型提供默认选项
      if (isHappyHorseModel) {
        // HappyHorse 默认：仅多模态参考（image-to-video 在 Toapis 上报错）
        configImageModes = ['reference-to-video'];
      } else {
        // Seedance 默认：单图、首尾帧、多模态
        configImageModes = ['first_frame', 'first_last_frame', 'multi_reference'];
      }
    }
    // 分辨率
    if (Array.isArray(parsedEnabledParams.resolution) && parsedEnabledParams.resolution.length > 0) {
      configResolutions = parsedEnabledParams.resolution;
    }
    // Seedance 生成音频
    // 2.5、2.0、2.0 Mini 和 2.0 Fast 固定有声，不给选项（连兜底也不能进，否则清空配置反而把下拉召回来）
    if (isSeedance25Model || isSeedance20Model || isSeedance20MiniModel || isSeedance20FastModel) {
      configGenerateAudio = null;
    } else if (Array.isArray(parsedEnabledParams.generate_audio) && parsedEnabledParams.generate_audio.length > 0) {
      configGenerateAudio = parsedEnabledParams.generate_audio;
    } else if (isSeedanceModel) {
      // 兜底：seedance 模型未配置时默认提供 true/false 两个选项
      configGenerateAudio = ['true', 'false'];
    }
    // 🆕 MJ 专用参数
    if (Array.isArray(parsedEnabledParams.quality) && parsedEnabledParams.quality.length > 0) {
      configQuality = dedupeWithNormalizer(parsedEnabledParams.quality);
    }
    if (Array.isArray(parsedEnabledParams.version) && parsedEnabledParams.version.length > 0) {
      const normalized = dedupeWithNormalizer(parsedEnabledParams.version, normalizeVersionValue);
      const selected = new Set(normalized.map((v) => String(v)));
      const orderedPreferred = preferredVersionOrder.filter((v) => selected.has(v));
      const extras = normalized.filter((v) => !preferredVersionOrder.includes(v));
      configVersion = [...orderedPreferred, ...extras];
    }

    // 回退：尝试从 extra_json 读取（兼容旧数据）
    if (!configRatios || !configSizes) {
        let extra = {};
        try {
            if (currentModel.extra_json) {
                if (typeof currentModel.extra_json === 'string') {
                    extra = JSON.parse(currentModel.extra_json);
                    if (typeof extra === 'string') extra = JSON.parse(extra);
                } else if (typeof currentModel.extra_json === 'object') {
                    extra = currentModel.extra_json;
                }
            }
        } catch (e) { 
            console.warn('parse extra_json failed', e); 
        }

        if (!configRatios && Array.isArray(extra.supported_ratios)) {
            configRatios = extra.supported_ratios;
        }
        if (!configSizes && Array.isArray(extra.supported_sizes)) {
            configSizes = extra.supported_sizes;
        }
    }
  }

  // Seedance 2.5 首帧/首尾帧：把节点上存着的非 adaptive 比例改正过来。
  // 服务端也会强制改写（provider 里兜底），这里同步是为了让面板显示与实际出片一致，
  // 不然下拉停在 16:9、成片却是跟随首帧的比例，看着像 bug。
  useEffect(() => {
    if (!isSeedance25FrameTask) return;
    const cur = data.params?.size;
    if (cur === 'adaptive') return;
    handleUpdate({ params: { ...(data.params || {}), size: 'adaptive' } });
  }, [isSeedance25FrameTask, data.params?.size]); // eslint-disable-line react-hooks/exhaustive-deps

  // 运镜指令：检查当前模型是否在 enabled_parameters 中配置了 camera_movement
  const hasCameraMovement = parsedEnabledParams.hasOwnProperty('camera_movement');

      useEffect(() => {
        if (!currentModel || !isAspectRatioSuppressedForCurrentModel) return;
        if (!data.params || !Object.prototype.hasOwnProperty.call(data.params, 'aspect_ratio')) return;

        const nextParams = { ...data.params };
        delete nextParams.aspect_ratio;
        handleUpdate({ params: nextParams });
      }, [currentModel, data.params, isAspectRatioSuppressedForCurrentModel, handleUpdate]);

  // 时长默认值 + 越界自动修正：
  //   - 未设置时 → 写入已启用时长的最小值（确保前端显示值与实际传给后端的一致）
  //   - 已设置但超出范围 → clamp 到最近的合法值
  const configDurationsKey = configDurations ? configDurations.join(',') : '';
  useEffect(() => {
    if (!configDurations || configDurations.length === 0) return;
    const sorted = [...configDurations].map(Number).sort((a, b) => a - b);
    const currentDur = data.params?.duration;
    if (!currentDur) {
      // 未设置 → 默认最低秒
      handleUpdate({ params: { ...(data.params || {}), duration: String(sorted[0]) } });
      return;
    }
    if (configDurations.includes(String(currentDur))) return; // 在范围内
    // 不在范围内 → clamp 到最近的合法值
    const numDur = Number(currentDur);
    const clamped = numDur < sorted[0] ? sorted[0] : numDur > sorted[sorted.length - 1] ? sorted[sorted.length - 1] : sorted.reduce((prev, curr) => Math.abs(curr - numDur) < Math.abs(prev - numDur) ? curr : prev);
    handleUpdate({ params: { ...(data.params || {}), duration: String(clamped) } });
  }, [configDurationsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🆕 素材增删后重算「生成模式」
  //
  // 踩坑：旧逻辑只在当前模式「不合法」时才纠正，于是减图不回弹 ——
  //   2 图选了多模态 → 拔掉 1 张 → multi_reference 在 1 图下依然合法 → 不纠正，
  //   模式卡在多模态。加图方向能自愈是因为 first_last_frame 在 1 图下非法。
  //
  // 修法：素材构成（图数/有无视频/有无音频）一变就回到推荐值。素材没变时
  //   完全不干预，用户手选的模式保留（比如 1 张图手动选多模态）。
  // 首次挂载不重置合法的已存模式，避免打开老项目被改写。
  //
  // 踩坑 2：换模型也必须重算。handleModelChange 会把 params 重置成新模型
  //   enabled_parameters 里每项的第 0 个值（image_mode 即 first_frame），
  //   而旧依赖里没有模型标识 —— 在两个都支持 image_mode 的模型之间切换
  //   （Seedance ↔ MiniMax-H3）时 effect 不重跑，模式就卡在 first_frame。
  //   3 张参考图下 first_frame 不在可选集里，下拉只剩占位符，得手选一次。
  const lastAssetSigRef = useRef(null);
  const lastModelKeyRef = useRef(null);
  const modelKey = String(data.modelId ?? '');
  // 可选集本身也随模型变（管理员可为每个模型单独启用 image_mode 的子集）
  const configImageModesKey = configImageModes ? configImageModes.join(',') : '';
  useEffect(() => {
    if (!supportsImageMode || !configImageModes || configImageModes.length === 0) return;

    const { availableModes, recommended } = computeImageModeOptions({
      imgCount: imageFiles.length,
      hasAudio: audioFiles.length > 0,
      hasVideo: videoFiles.length > 0,
      isHappyHorse: isHappyHorseModel,
      configImageModes,
    });
    if (availableModes.length === 0) {
      lastAssetSigRef.current = assetSignature;
      lastModelKeyRef.current = modelKey;
      return;
    }

    // 首次挂载（两个 ref 都还没写过）不主动改写已存的合法模式
    const isFirstRun = lastAssetSigRef.current === null && lastModelKeyRef.current === null;
    const sigChanged = !isFirstRun && lastAssetSigRef.current !== assetSignature;
    const modelChanged = !isFirstRun && lastModelKeyRef.current !== modelKey;
    lastAssetSigRef.current = assetSignature;
    lastModelKeyRef.current = modelKey;

    const currentMode = data.params?.image_mode;
    const isValid = currentMode && availableModes.includes(currentMode);

    // 素材变了 或 换了模型 → 回推荐值；否则仅在缺失/非法时兜底（与旧行为一致）
    const needsReset = sigChanged || modelChanged;
    if (!needsReset && isValid) return;

    const target = (needsReset ? recommended : (isValid ? currentMode : recommended))
      || availableModes[0];
    if (target && target !== currentMode) {
      handleUpdate({ params: { ...(data.params || {}), image_mode: target } });
    }
  }, [assetSignature, supportsImageMode, isHappyHorseModel, modelKey, configImageModesKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seedance 2.5：清掉节点上残留的 generate_audio
  // 下拉藏起来之后，之前存过「无声」的老节点仍会把 generate_audio:'false' 一路发到后端。
  // 产物不会错（provider 在 2.5 分支写死 true），但扣费会错：
  // server/routes/ai.js 的倍率是拿 params[key] 去查 _multipliers 的，
  // 后台若还留着无声档倍率，就会按无声价扣钱、按有声出片。这里顺手把这个键抹掉。
  useEffect(() => {
    if (!isSeedance25Model) return;
    if (data.params?.generate_audio === undefined) return;
    handleUpdate({ params: { ...(data.params || {}), generate_audio: undefined } });
  }, [isSeedance25Model, data.params?.generate_audio]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasAnyConfiguredParam = Object.keys(parsedEnabledParams).some((key) => key !== '_multipliers' && key !== '_dependsOn');
  const imageBuiltInParamKeys = new Set(['aspect_ratio', 'image_size', 'quality', 'version', 'lora', 'lora_strength']);

  // 🔧 稳定化 modelParamOrder，避免每次渲染都创建新数组
  // 🔧 稳定化 modelParamOrder，避免对象引用变化导致无效重计算
  const parametersKey = JSON.stringify(currentModel?.parameters || {});
  const modelParamOrder = React.useMemo(() => {
    return Object.keys(currentModel?.parameters || {});
  }, [currentModel?.id, parametersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * 参数联动（cascade）配置：来自 enabled_parameters._dependsOn
   * 格式：{ [childKey]: { field: parentKey, validOptions: { [parentValue]: [childValue, ...] } } }
   * 例：{ size: { field: 'resolution', validOptions: { '1K': ['1:1','3:2','2:3'], '4K': ['16:9','9:16'] } } }
   * 作用：当父参数值变化时，子参数下拉列表仅展示对应的合法选项，不兼容的当前值自动重置。
   */
  const paramDependsOn = parsedEnabledParams._dependsOn || null;

  // 🆕 动态参数：支持 select（数组）、boolean、lora 和 number 类型
  // 🔧 稳定化 dynamicImageParams，避免每次渲染都创建新数组
  const dynamicImageParams = React.useMemo(() => {
    const params = modelParamOrder.length > 0
      ? modelParamOrder
          .filter((key) => key !== '_multipliers' && key !== '_dependsOn' && !imageBuiltInParamKeys.has(key))
          .map((key) => {
            const paramDef = currentModel?.parameters?.[key];
            const values = parsedEnabledParams[key];
            // boolean 类型：优先检查（无需在 parsedEnabledParams 里）
            if (paramDef?.type === 'boolean') {
              return { key, type: 'boolean', default: paramDef.default };
            }
            // lora 类型：带 LoRA 模型列表
            if (paramDef?.type === 'lora') {
              return { key, type: 'lora', loras: paramDef.loras || [], default: paramDef.default };
            }
            // number 类型：数字输入（带范围）
            if (paramDef?.type === 'number') {
              return {
                key,
                type: 'number',
                min: paramDef.min,
                max: paramDef.max,
                default: paramDef.default
              };
            }
            // select 类型：values 是数组
            if (Array.isArray(values) && values.length > 0) {
              return { key, type: 'select', values };
            }
            return null;
          })
          .filter(Boolean)
      : Object.entries(parsedEnabledParams)
          .filter(([key, values]) => key !== '_multipliers' && key !== '_dependsOn' && !imageBuiltInParamKeys.has(key))
          .map(([key, values]) => {
            // boolean 类型：检测 [true, false] 数组模式（包括字符串形式）
            if (Array.isArray(values) && values.length === 2) {
              const hasTrue = values.includes(true) || values.includes('true');
              const hasFalse = values.includes(false) || values.includes('false');
              if (hasTrue && hasFalse) {
                return { key, type: 'boolean', default: false };
              }
            }
            // select 类型：values 是数组
            if (Array.isArray(values) && values.length > 0) {
              return { key, type: 'select', values };
            }
            return null;
          })
          .filter(Boolean);
    return params;
  }, [modelParamOrder, parsedEnabledParams, parametersKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const videoBuiltInParamKeys = new Set(['aspect_ratio', 'duration', 'size', 'template_id', 'camera_strength', 'image_mode', 'resolution', 'camera_movement', 'generate_audio', 'ratio']);
  // 🔧 稳定化 dynamicVideoParams，避免每次渲染都创建新数组
  const dynamicVideoParams = React.useMemo(() => {
    const params = modelParamOrder.length > 0
      ? modelParamOrder
          .filter((key) => key !== '_multipliers' && key !== '_dependsOn' && !videoBuiltInParamKeys.has(key))
          .map((key) => {
            const paramDef = currentModel?.parameters?.[key];
            const values = parsedEnabledParams[key];
            // 过滤掉 autoTrigger 参数（自动触发，不显示在节点控制台）
            if (paramDef?.autoTrigger) {
              return null;
            }
            // boolean 类型：优先检查（无需在 parsedEnabledParams 里）
            if (paramDef?.type === 'boolean') {
              return { key, type: 'boolean', default: paramDef.default };
            }
            // select 类型：values 是数组
            if (Array.isArray(values) && values.length > 0) {
              return { key, type: 'select', values };
            }
            return null;
          })
          .filter(Boolean)
      : Object.entries(parsedEnabledParams)
          .filter(([key, values]) => key !== '_multipliers' && key !== '_dependsOn' && !videoBuiltInParamKeys.has(key))
          .map(([key, values]) => {
            // boolean 类型：检测 [true, false] 数组模式（包括字符串形式）
            if (Array.isArray(values) && values.length === 2) {
              const hasTrue = values.includes(true) || values.includes('true');
              const hasFalse = values.includes(false) || values.includes('false');
              if (hasTrue && hasFalse) {
                return { key, type: 'boolean', default: false };
              }
            }
            // select 类型：values 是数组
            if (Array.isArray(values) && values.length > 0) {
              return { key, type: 'select', values };
            }
            return null;
          })
          .filter(Boolean);
    return params;
  }, [modelParamOrder, parsedEnabledParams, parametersKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🆕 修改逻辑：只显示模型明确配置的参数
  // 如果没有选中模型，则不显示参数（ratiosToRender 为空）
  // 如果选中了模型但未配置参数，则显示默认的所有比例（ALL_RATIOS）
  const ratiosToRender = isAspectRatioSuppressedForCurrentModel
    ? []
    : (configRatios !== null ? configRatios : (currentModel && !hasAnyConfiguredParam ? ALL_RATIOS : []));
  // 尺寸只有在明确配置时才显示，null 表示模型不支持尺寸参数
  const sizesToRender = configSizes !== null ? configSizes : [];
  const versionOptionsSource = (configVersion && configVersion.length > 0)
    ? [
        // 保持首选顺序，但只包含管理员配置的版本
        ...preferredVersionOrder.filter((v) => (configVersion || []).map(String).includes(String(v))),
        // 把额外的、自定义顺序的版本追加在后面
        ...((configVersion || []).filter((v) => !preferredVersionOrder.includes(v)))
      ]
    : [];

  /**
   * 切换模型回调
   *
   * 职责：
   *   1. 解析新模型的 enabled_parameters，提取每个参数的第一项作为默认值
   *   2. 同步更新节点预览尺寸 —— 如果新模型的默认比例与当前不同，
   *      立即调用 ratioToPreviewSize() 计算 customSize 并触发 _ratioPreview
   *      动画过渡，确保预览区与右侧下拉框始终一致
   *
   * 参数初始化规则：
   *   - aspect_ratio : 取 enabled_parameters 第一项；图像模型无配置时 fallback ALL_RATIOS[0]
   *   - image_size   : 仅 Pro 模型配置，如 "1K"/"2K"/"4K"
   *   - duration     : 视频模型时长，如 "5"/"10"
   *   - size         : 部分模型通用尺寸参数
   *   - template_id  : 运镜模板 ID
   *   - camera_strength : 运镜强度
   *
   * 预览尺寸联动（仅在节点尚未生成内容时生效）：
   *   切换模型 → 新默认比例 → ratioToPreviewSize() → customSize + _ratioPreview
   *   与比例下拉框 onChange 里的逻辑完全一致（见下方 DropdownSelector onChange）
   */
  // 📝 文本润色处理
  const polishAbortRef = useRef(null);

  const handlePolish = useCallback(async (textToPolish) => {
    const text = (textToPolish || data.prompt || '').trim();
    if (!text) return;
    setPolishPreview({ original: text, polished: '' });
    setIsPolishing(true);

    try {
      const token = getToken();
      const abortController = new AbortController();
      polishAbortRef.current = abortController;

      const response = await fetch(`${API_BASE_URL}/api/ai/text-polish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text }),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `请求失败 (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (payload === '[DONE]') break;
          try {
            const json = JSON.parse(payload);
            if (json.error) throw new Error(json.error);
            if (json.content) {
              accumulated += json.content;
              setPolishPreview(prev => prev ? { ...prev, polished: accumulated } : null);
            }
          } catch (e) {
            if (e.message && e.message !== 'Unexpected end of JSON input') {
              console.error('润色流解析错误:', e);
            }
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('润色失败:', e);
      setPolishPreview(prev => prev ? { ...prev, polished: `[错误] ${e.message || '整理失败'}` } : null);
    } finally {
      setIsPolishing(false);
      polishAbortRef.current = null;
    }
  }, [data.prompt]);

  const handlePolishConfirm = useCallback((finalText) => {
    if (finalText) {
      handleUpdate({ prompt: finalText });
    }
    setPolishPreview(null);
  }, [handleUpdate]);

  const handlePolishCancel = useCallback(() => {
    if (polishAbortRef.current) {
      polishAbortRef.current.abort();
    }
    setPolishPreview(null);
    setIsPolishing(false);
  }, []);

  const handleModelChange = (val) => {
    const model = models.find(m => String(m.id) === String(val));
    if (!model) {
      handleUpdate({ modelId: val, params: {}, modelName: '' });
      return;
    }

    // ── 解析模型的 enabled_parameters（管理后台配置的可用参数列表） ──
    let enabledParams = {};
    try {
      if (model.enabled_parameters) {
        enabledParams = typeof model.enabled_parameters === 'string' 
          ? JSON.parse(model.enabled_parameters) 
          : model.enabled_parameters;
      }
    } catch (e) { /* enabled_parameters 损坏时静默降级为空 */ }

    // ── 为每个参数取第一项作为默认值 ──
    // 🔧 优先保留当前已设置的值（同类型模型切换时保持用户选择），
    //    新参数或当前值不在新模型选项中时才用第一项
    const newParams = {};
    const currentParams = data.params || {};

    // 按配置顺序为每个参数取第一项作为默认值
    Object.entries(enabledParams).forEach(([key, values]) => {
      if (key === '_multipliers' || key === '_dependsOn') return;
      if (Array.isArray(values) && values.length > 0) {
        // 如果当前值在新模型的选项中，保留；否则用第一项
        let currentValue = currentParams[key];

        // 🔧 比例参数兼容：aspect_ratio ↔ ratio 互通
        if ((key === 'aspect_ratio' || key === 'ratio') && currentValue === undefined) {
          currentValue = currentParams['aspect_ratio'] || currentParams['ratio'];
        }

        if (currentValue !== undefined && values.includes(currentValue)) {
          newParams[key] = currentValue;
        } else {
          newParams[key] = values[0];
        }
      }
    });

    if (isAspectRatioSuppressedModel(model)) {
      delete newParams.aspect_ratio;
    }

    // 仅兼容旧模型（完全没有参数配置）时才回退默认比例
    const hasConfiguredParams = Object.keys(enabledParams).some((key) => key !== '_multipliers' && key !== '_dependsOn');
    if (!newParams.aspect_ratio && model.model_type === 'image' && !hasConfiguredParams && !isAspectRatioSuppressedModel(model)) {
      newParams.aspect_ratio = ALL_RATIOS[0];
    }

    // ── 构建更新载荷 ──
    const updatePayload = { 
      modelId: val, 
      params: newParams, 
      modelName: model.name,
      modelLogo: model.logo_url 
    };

    if (isImageBlenderModel(model)) {
      updatePayload.prompt = '';
      setLocalPrompt('');
    }

    // MJ Imagine / Blend 固定出 4 图，锁定批次为 1
    if (isMjBatchLockedModel(model)) {
      updatePayload.batchCount = 1;
    }

    // ── 预览尺寸联动：切换模型时按新模型的默认比例调整节点朝向 ──
    // 使用 ratioToPreviewSizeWithCurrent（保留当前长边，仅改变比例朝向），
    // 与比例下拉框 onChange 的逻辑完全一致，避免旧版 ratioToPreviewSize 把节点缩成固定小尺寸。
    // 仅在节点尚未生成内容时生效（已有产出则保持原尺寸，不打断用户）。
    const previewRatioSource = newParams.aspect_ratio || newParams.ratio || newParams.size;
    if (!data.output && previewRatioSource) {
      // 兜底：首次选模型时 customSize 可能尚未设置，用节点默认尺寸 320×240
      const baseSize = data.customSize || { width: 320, height: 240 };
      const sz = ratioToPreviewSizeWithCurrent(previewRatioSource, baseSize, data.storyboardMode);
      if (sz) {
        updatePayload.customSize = sz;
        updatePayload._ratioPreview = true; // 触发 CSS transition 动画
      }
    }

    handleUpdate(updatePayload);
  };

  // 🎨 预设限定模型后，若节点当前模型不在白名单内（如新建时套用了「上次模型」记忆，
  //   或预设改了勾选），自动切到白名单第一个模型并套用其默认参数，避免下拉显示不可选项。
  const handleModelChangeRef = useRef(handleModelChange);
  handleModelChangeRef.current = handleModelChange;
  useEffect(() => {
    if (!isImageGenNode || loadingModels) return;
    if (presetModelIds.length === 0 || visibleModels.length === 0) return;
    const ok = visibleModels.some(m => String(m.id) === String(data.modelId));
    if (!ok) handleModelChangeRef.current(visibleModels[0].id);
  }, [isImageGenNode, loadingModels, data.modelId, visibleModels, presetModelIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div ref={consolePanelRef} className={`custom-scrollbar${consolePinned ? ' console-pinned' : ''}`} style={{
      ...styles.consolePanel(zoom),
      // 🎬 分镜节点：取消抗缩放（跟随画布缩放），宽度由内容（宫格+解析区两列）自适应撑开
      ...(data.storyboardMode ? {
        width: 'fit-content',
        minWidth: '480px',
        maxWidth: '1300px',
        transform: 'translateX(-50%)',
      } : {}),
    }}>
      {/* 📌 钉住按钮 - 控制台右上角，紧凑不占空间 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-12px', marginRight: '-12px', marginBottom: '-2px', height: '16px', position: 'relative', zIndex: 30 }}>
        <QuickTooltip content={consolePinned ? "取消钉住" : "钉住控制台"} position="top">
          <div
            className="nodrag"
            onClick={onTogglePin}
            style={{
              width: '20px', height: '20px',
              borderRadius: '50%',
              background: consolePinned ? 'var(--bg-hover)' : 'transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={e => { if (!consolePinned) e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { if (!consolePinned) e.currentTarget.style.background = 'transparent'; }}
          >
            <Pin size={11} style={{
              color: consolePinned ? 'var(--pin-active-fg)' : '#999',
              transform: consolePinned ? 'rotate(0deg)' : 'rotate(45deg)',
              transition: 'all 0.2s'
            }} />
          </div>
        </QuickTooltip>
      </div>
      {(data.refFiles?.length > 0 || data.refAudios?.length > 0 || data.refTexts?.length > 0 || activeLoraTags.length > 0 || activeRefImageTag) && (
        <div style={styles.assetRow} className="custom-scrollbar">
          {imageFiles.map((file, idx) => (
            <div 
              key={`img-${idx}`} 
              style={styles.imageItem} 
              className="nodrag"
              draggable="true"
              onDragStart={(e) => handleDragStart(e, 'image', file.url, file.title)}
            >
              <div style={styles.indexBadge('#3b82f6')}>{idx + 1}</div>
              <ShimmerThumb src={file.localCacheUrl || file.mini_thumbnail_url || file.thumbUrl || file.url} fallbackSrc={file.url} style={styles.thumbImg} />
              <div style={styles.closeBtn} onClick={() => data.onRemoveRefFile(id, file.id)}><X size={10} color="#fff" /></div>
              {/* 🆕 描述词开关（右下角）：与音频卡片同一套逻辑 */}
              <div
                style={{
                  ...descBadgeStyle(REF_BADGE_H, file.description && file.descEnabled !== false),
                  position: 'absolute', bottom: 0, right: 0, zIndex: 11,
                }}
                title={file.descEnabled !== false
                  ? `描述词已进提示词（点击关闭）${file.description ? `：${file.description}` : '(当前资产无描述词)'}`
                  : `描述词未进提示词（点击开启）${file.description ? `：${file.description}` : '(当前资产无描述词)'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  const newDescEnabled = file.descEnabled === false;
                  console.log('[T角标] 点击切换，当前图片:', file);
                  console.log('[T角标] descEnabled 切换:', file.descEnabled, '→', newDescEnabled);
                  const updatedRefFiles = (data.refFiles || []).map(f =>
                    f.id === file.id ? { ...f, descEnabled: newDescEnabled } : f);
                  console.log('[T角标] 更新后的 refFiles:', updatedRefFiles);
                  handleUpdate({
                    refFiles: updatedRefFiles,
                  });
                }}
                onMouseEnter={descBadgeHover.onMouseEnter(file.description && file.descEnabled !== false)}
                onMouseLeave={descBadgeHover.onMouseLeave(file.description && file.descEnabled !== false)}
              >
                <DescriptionIcon size={9} />
              </div>
            </div>
          ))}
          {videoFiles.map((file, idx) => (
            <div 
              key={`vid-${idx}`} 
              style={styles.imageItem} 
              className="nodrag"
              draggable="true"
              onDragStart={(e) => handleDragStart(e, 'video', file.url, file.title)}
            >
              <div style={styles.indexBadge('#fbbf24')}>{idx + 1}</div>
              <ShimmerThumb src={file.localCacheUrl || file.mini_thumbnail_url || file.thumbUrl || file.url} fallbackSrc={file.url} isVideo style={styles.thumbImg} />
              <div style={styles.closeBtn} onClick={() => data.onRemoveRefFile(id, file.id)}><X size={10} color="#fff" /></div>
              {/* 🆕 描述词开关（右下角）：与图片卡片同一套逻辑 */}
              <div
                style={{
                  ...descBadgeStyle(REF_BADGE_H, file.description && file.descEnabled !== false),
                  position: 'absolute', bottom: 0, right: 0, zIndex: 11,
                }}
                title={file.descEnabled !== false
                  ? `描述词已进提示词（点击关闭）${file.description ? `：${file.description}` : '(当前资产无描述词)'}`
                  : `描述词未进提示词（点击开启）${file.description ? `：${file.description}` : '(当前资产无描述词)'}`}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('[T角标] 点击切换，当前视频:', file);
                  handleUpdate({
                    refFiles: (data.refFiles || []).map(f =>
                      f.id === file.id ? { ...f, descEnabled: f.descEnabled === false } : f),
                  });
                }}
                onMouseEnter={descBadgeHover.onMouseEnter(file.description && file.descEnabled !== false)}
                onMouseLeave={descBadgeHover.onMouseLeave(file.description && file.descEnabled !== false)}
              >
                <DescriptionIcon size={9} />
              </div>
            </div>
          ))}
          {data.refAudios?.map((audio, i) => (
            <AudioCardTile
              key={`audio-${i}`}
              audioObj={audio}
              index={i + 1}
              onRemove={() => data.onRemoveRefAudio(id, audio.id)}
              onToggleDesc={(audioId) => {
                handleUpdate({
                  refAudios: (data.refAudios || []).map(a =>
                    a.id === audioId ? { ...a, descEnabled: a.descEnabled === false } : a),
                });
              }}
            />
          ))}
          {data.refTexts?.map((t, i) => (
            <div 
              key={`txt-${i}`} 
              style={styles.textItem} 
              className="nodrag"
              draggable="true"
              onDragStart={(e) => handleDragStart(e, 'text', t.text, t.title)}
            >
              <div style={styles.textTile(t.bgColor)}>
                <Sparkles size={14} color="rgba(255,255,255,0.5)" strokeWidth={1.5} />
                <div style={styles.textTileTitle}>{t.title || "咒语"}</div>
              </div>
              <div style={styles.closeBtn} onClick={() => data.onRemoveRefText(id, t.id)}><X size={10} color="#fff" /></div>
            </div>
          ))}
          {/* LoRA 方块：从 prompt 中的 #标签# 提取 */}
          {activeLoraTags.map((lora, idx) => (
            <div
              key={`lora-${idx}`}
              style={{
                position: 'relative',
                width: '60px',
                minWidth: '60px',
                height: '60px',
                border: '1px solid var(--border-subtle)',
                flexShrink: 0,
                marginRight: '8px',
              }}
              className="nodrag"
              data-lora-tile
            >
              <div
                onClick={() => setActiveLoraSlider(activeLoraSlider === lora.name ? null : lora.name)}
                style={{
                  cursor: 'pointer',
                  width: '100%',
                  height: '100%',
                }}
              >
                <div style={{
                  ...styles.textTile('#1a2332'), // 深灰蓝色背景
                  position: 'relative',
                  border: '1px solid rgba(100, 116, 139, 0.3)', // 灰色边框
                  display: 'flex',
                  alignItems: 'flex-end', // 底部对齐
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {/* 缩略图背景 */}
                  {lora.thumbnail && (
                    <img
                      src={lora.thumbnail}
                      alt=""
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.6,
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {/* 左上角 LoRA 标签 */}
                  <div style={{
                    position: 'absolute',
                    top: 3,
                    left: 3,
                    background: 'rgba(100, 116, 139, 0.8)', // 灰蓝色
                    borderRadius: 0,
                    padding: '1px 3px',
                    fontSize: '8px',
                    color: '#fff',
                    lineHeight: 1.2,
                    fontWeight: 600,
                    zIndex: 1,
                  }}>
                    LoRA
                  </div>
                  {/* 右上角关闭按钮 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      opacity: 0.7,
                      transition: 'opacity 0.2s',
                      zIndex: 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeLoraTag(lora.name);
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                  >
                    <X size={10} color="#fff" />
                  </div>
                  {/* 文字绝对定位在底部 */}
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '2px',
                    right: '2px',
                    fontSize: '10px',
                    color: 'var(--text-body)',
                    fontWeight: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}>
                    {lora.label}
                  </div>
                  {/* 强度指示器 */}
                  <div style={{
                    position: 'absolute',
                    bottom: '1px',
                    left: '2px',
                    right: '2px',
                    height: '2px',
                    background: 'rgba(100, 116, 139, 0.3)',
                    borderRadius: '1px',
                  }}>
                    <div style={{
                      width: `${((lora.strength - 0.1) / 1.9) * 100}%`,
                      height: '100%',
                      background: '#fbbf24', // 黄色进度条
                      borderRadius: '1px',
                      transition: 'width 0.2s',
                    }} />
                  </div>
                </div>
              </div>
              {/* 点击时弹出的强度调节器 */}
              {activeLoraSlider === lora.name && (
                <div
                  className="nodrag"
                  data-lora-slider
                  style={{
                    position: 'absolute',
                    top: '68px',
                    left: '0',
                    zIndex: 1000,
                    background: 'var(--bg-hover)',
                    border: '1px solid var(--border-strong)',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    backdropFilter: 'blur(8px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                    minWidth: '160px',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      强度
                    </span>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="1"
                      value={Math.round(lora.strength * 10)}
                      onChange={(e) => {
                        const val = parseInt(e.target.value);
                        const strength = val / 10;
                        setLoraStrength(lora.name, strength);
                      }}
                      style={{
                        flex: 1,
                        height: '3px',
                        borderRadius: '2px',
                        background: 'var(--border-subtle)',
                        outline: 'none',
                        cursor: 'pointer',
                      }}
                    />
                    <span style={{
                      fontSize: '11px',
                      color: 'var(--text-primary)',
                      fontWeight: 600,
                      minWidth: '32px',
                      textAlign: 'right'
                    }}>
                      {lora.strength.toFixed(1)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* 🆕 参考图方块：显示当前选中的参考图 */}
          {activeRefImageTag && (
            <div
              key={`refimage-${activeRefImageTag.id}`}
              style={{
                position: 'relative',
                width: '60px',
                minWidth: '60px',
                height: '60px',
                border: '1px solid var(--border-subtle)',
                flexShrink: 0,
                marginRight: '8px',
              }}
              className="nodrag"
              data-refimage-tile
            >
              <div
                style={{
                  cursor: 'pointer',
                  width: '100%',
                  height: '100%',
                }}
              >
                <div style={{
                  ...styles.textTile('#1a2332'),
                  position: 'relative',
                  border: '1px solid rgba(100, 116, 139, 0.3)',
                  display: 'flex',
                  alignItems: 'flex-end',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {/* 缩略图背景 */}
                  {activeRefImageTag.thumbnailUrl && (
                    <img
                      src={activeRefImageTag.thumbnailUrl}
                      alt=""
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: 0.8,
                      }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  {/* 左上角参考图标签 */}
                  <div style={{
                    position: 'absolute',
                    top: 3,
                    left: 3,
                    background: 'rgba(59, 130, 246, 0.9)',
                    borderRadius: 0,
                    padding: '1px 3px',
                    fontSize: '8px',
                    color: '#fff',
                    lineHeight: 1.2,
                    fontWeight: 600,
                    zIndex: 1,
                  }}>
                    {refImageLabel}
                  </div>
                  {/* 右上角关闭按钮 */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      width: '14px',
                      height: '14px',
                      borderRadius: '50%',
                      background: 'rgba(0,0,0,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      opacity: 0.7,
                      transition: 'opacity 0.2s',
                      zIndex: 1,
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeRefImageTag();
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.7'}
                  >
                    <X size={10} color="#fff" />
                  </div>
                  {/* 文字绝对定位在底部 */}
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    left: '2px',
                    right: '2px',
                    fontSize: '10px',
                    color: 'var(--text-body)',
                    fontWeight: 400,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'center',
                  }}>
                    {activeRefImageTag.name}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {autoExplanation && <div style={styles.semanticRow}>{autoExplanation}</div>}

      {/* 🆕 分镜节点：用专用编辑器替换 prompt textarea
          数据存在 data.basePrompts / data.gridCells / data.gridType 上，
          生成时由 useNodeActions 的 handleGenerate 拼成完整 prompt 传给模型 */}
      {data.storyboardMode ? (
        <StoryboardEditor data={data} handleUpdate={handleUpdate} nodeId={id} />
      ) : (
      <>
      {/* 🆕 Slate.js 富文本编辑器：支持内联气泡 */}
      <div style={{ position: 'relative' }}>
        <SlatePromptEditor
          value={localPrompt}
          slateValue={localSlateValue}
          onChange={handlePromptChange}
          onKeyDown={handleSlateKeyUp}
          onFocus={() => {
            // 🔑 移除了 setLocalPrompt(data.prompt || '');
            // 因为这会在获得焦点时重置编辑器内容，导致用户输入丢失
            setIsPromptFocused(true);
            setActiveDropdown(null);
            setCommandState(null);
          }}
          onBlur={() => {
            // 🔑 先保存当前值，再设置焦点状态
            // 因为 setIsPromptFocused(false) 会触发 useEffect，可能重置 localPrompt
            const currentValue = localPrompt;
            const currentSlateValue = localSlateValue;
            setIsPromptFocused(false);
            clearTimeout(debounceTimerRef.current);
            handleUpdate({ prompt: currentValue, slateValue: currentSlateValue });
            clearTimeout(mentionTimerRef.current);
            mentionTimerRef.current = setTimeout(() => setCommandState(null), 150);
          }}
          placeholder={isPromptUnsupportedForCurrentModel ? promptUnsupportedText : ([presetPlaceholder, skillboxPlaceholder].filter(Boolean).join('\n') || '在此输入 Prompt 指令，输入 @ 可引用素材库，输入 / 可插入咒语...')}
          disabled={isPromptUnsupportedForCurrentModel}
          refFiles={[...imageFiles, ...videoFiles, ...audioFiles]}
          assets={promptMentionAssets} // 🆕 文本里的 @资产名 自动转气泡
          promptRef={promptRef}
          style={{
            ...styles.consoleTextarea,
            minHeight: '84px',
            maxHeight: '220px', // 约 10 行文本（fontSize 13px * lineHeight 1.6 * 10 行 + padding）
            overflowY: 'auto',
          }}
          className="nodrag nowheel custom-scrollbar"
        />

        {/* @ / 联想下拉 */}
        {commandState !== null && (
          <MentionDropdown
            query={commandState.query}
            assets={activeCommandType === 'slash' ? slashAssets
              : mentionAssets}
            title={dropdownTitle}
            filterAsset={dropdownFilter}
            onSelect={activeCommandType === 'slash' ? handleSlashSelect
              : handleMentionSelect}
            onClose={() => setCommandState(null)}
          />
        )}

        {/* 右下角悬浮按钮组：润色 + 语音输入 + 全屏编辑 */}
        <div style={{ position: 'absolute', right: '8px', bottom: '8px', display: 'flex', gap: '4px', alignItems: 'center' }}>
          {/* 📝 文本润色按钮 */}
          {textPolishEnabled && (
            <QuickTooltip content={isPolishing ? '整理中...' : '文本整理'} position="top" delay={300}>
              <div
                className="nodrag"
                onClick={() => { if (!isPolishing && (data.prompt || '').trim()) handlePolish(); }}
                style={{
                  width: '28px', height: '28px', borderRadius: '6px',
                  background: isPolishing ? 'rgba(96,165,250,0.2)' : 'var(--bg-hover)',
                  border: `1px solid ${isPolishing ? 'rgba(96,165,250,0.4)' : 'var(--border-strong)'}`,
                  cursor: isPolishing ? 'wait' : (data.prompt || '').trim() ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s', backdropFilter: 'blur(4px)',
                  opacity: (data.prompt || '').trim() ? 1 : 0.45,
                }}
                onMouseEnter={(e) => { if (!isPolishing) { e.currentTarget.style.background = 'rgba(96,165,250,0.15)'; e.currentTarget.style.borderColor = 'rgba(96,165,250,0.4)'; }}}
                onMouseLeave={(e) => { if (!isPolishing) { e.currentTarget.style.background = 'var(--bg-hover)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}}
              >
                {isPolishing ? <Loader2 size={14} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} /> : <Wand2 size={14} color="#60a5fa" strokeWidth={2} />}
              </div>
            </QuickTooltip>
          )}

          {/* 🎤 语音输入按钮 */}
          <QuickTooltip content={isPromptUnsupportedForCurrentModel ? promptUnsupportedText : '语音输入'} position="top" delay={300}>
            <div style={{ opacity: isPromptUnsupportedForCurrentModel ? 0.45 : 1, pointerEvents: isPromptUnsupportedForCurrentModel ? 'none' : 'auto' }}>
              <VoiceMicButton
                size={28}
                position="floating"
                currentText={data.prompt || ''}
                textareaRef={promptRef}
                onResult={(newText) => handleUpdate({ prompt: newText })}
              />
            </div>
          </QuickTooltip>

          {/* 全屏编辑按钮 */}
          <QuickTooltip content={isPromptUnsupportedForCurrentModel ? promptUnsupportedText : '全屏编辑'} position="top" delay={300}>
            <div
              className="nodrag"
              onClick={() => {
                if (isPromptUnsupportedForCurrentModel) return;
                setIsFullscreenPrompt(true);
              }}
              style={{
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                background: 'var(--bg-hover)',
                border: '1px solid var(--border-strong)',
                cursor: isPromptUnsupportedForCurrentModel ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(4px)',
                opacity: isPromptUnsupportedForCurrentModel ? 0.45 : 1
              }}
              onMouseEnter={(e) => {
                if (isPromptUnsupportedForCurrentModel) return;
                e.currentTarget.style.background = 'var(--bg-hover)';
                e.currentTarget.style.borderColor = 'var(--text-hover)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                if (isPromptUnsupportedForCurrentModel) return;
                e.currentTarget.style.background = 'var(--bg-hover)';
                e.currentTarget.style.borderColor = 'var(--border-strong)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <Maximize2 size={14} color="var(--text-secondary)" strokeWidth={2} />
            </div>
          </QuickTooltip>
        </div>
      </div>
      </>
      )}

      <div style={styles.footer}>
        {/* 左侧：模型选择器 + 动态参数 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'nowrap', flex: 1, minWidth: 0 }}>
          <DropdownSelector 
            id="model-selector"
            activeId={activeDropdown}
            setActiveId={setActiveDropdown}
            value={data.modelId || ''}
            options={visibleModels.map(m => ({ value: m.id, label: m.name, logo: m.logo_url, description: m.description }))}
            onChange={handleModelChange}
            placeholder={loadingModels ? '加载中…' : (loadModelsError ? loadModelsError : '选择模型')}
            title="选择模型"
            showPlaceholderAsOption={true}
            showLogo={true}
            onOpen={() => {
              // 加载失败或空列表时，点击即重试
              if ((loadModelsError || models.length === 0) && !loadingModels) {
                loadModels(true);
              }
            }}
          />

          {/* 通用参数：仅图像节点显示 */}
          {data.type === 'image' && (
            <>
              {ratiosToRender.length > 0 && (
                <DropdownSelector
                  id="ratio-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && data.params.aspect_ratio) || ''}
                  options={ratiosToRender.map(r => ({ value: r, label: r }))}
                  onChange={(val) => {
                    const update = { params: { ...(data.params||{}), aspect_ratio: val || undefined } };
                    // 修改比例时根据当前尺寸调整节点大小
                    if (!data.output) {
                      const sz = ratioToPreviewSizeWithCurrent(val, data.customSize, data.storyboardMode);
                      if (sz) { update.customSize = sz; update._ratioPreview = true; }
                    }
                    handleUpdate(update);
                  }}
                  placeholder="比例"
                  title="比例"
                />
              )}

              {/* LoRA 按钮：仅 Z-Image 模型显示 */}
              {currentModel?.parameters?.lora && (
                <>
                  <button
                    ref={loraButtonRef}
                    data-lora-button
                    className="nodrag"
                    onClick={() => setLoraListVisible(!loraListVisible)}
                    style={{
                      padding: '6px 10px',
                      fontSize: '11px',
                      background: loraListVisible ? 'var(--bg-hover)' : 'transparent',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      whiteSpace: 'nowrap',
                    }}
                    onMouseEnter={(e) => {
                      if (!loraListVisible) e.currentTarget.style.background = 'var(--bg-hover)';
                    }}
                    onMouseLeave={(e) => {
                      if (!loraListVisible) e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    LoRA
                  </button>

                  {/* LoRA 列表弹窗 - 放在参数行外面，相对于整个控制台定位 */}
                  {loraListVisible && (
                    <div
                      data-lora-list-popup
                      style={{
                        position: 'absolute',
                        top: loraButtonRef.current ? `${loraButtonRef.current.offsetTop + loraButtonRef.current.offsetHeight + 4}px` : '100%',
                        left: 0,
                        right: 0,
                        zIndex: 9999
                      }}
                      onClick={(e) => {
                        console.log('[LoRA Container] onClick triggered');
                        e.stopPropagation();
                      }}
                    >
                      <MentionDropdown
                        query=""
                        assets={loraAssets}
                        title="LoRA 模型"
                        filterAsset={(asset) => asset?.type === 'lora'}
                        onSelect={(asset) => {
                          console.log('[NodeConsole] onSelect callback triggered with asset:', asset);
                          handleLoraSelect(asset);
                          setLoraListVisible(false);
                        }}
                        onClose={() => setLoraListVisible(false)}
                      />
                    </div>
                  )}
                </>
              )}

              {sizesToRender.length > 0 && (
                <DropdownSelector 
                  id="size-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && data.params.image_size) || ''}
                  options={sizesToRender.map(s => ({ value: s, label: s }))}
                  onChange={(val) => handleUpdate({ params: { ...(data.params||{}), image_size: val || undefined } })}
                  placeholder="尺寸"
                  title="尺寸（pro）"
                />
              )}

              {/* 🆕 MJ 专用参数：质量、版本 */}
              {configQuality && configQuality.length > 0 && (
                <DropdownSelector 
                  id="quality-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && data.params.quality) || ''}
                  options={configQuality.map(q => ({ value: q, label: q }))}
                  onChange={(val) => handleUpdate({ params: { ...(data.params||{}), quality: val || undefined } })}
                  placeholder="质量"
                  title="MJ 质量 (--q)"
                />
              )}
              
              {configVersion && configVersion.length > 0 && (
                (() => {
                  const versionOptions = dedupeWithNormalizer(versionOptionsSource, normalizeVersionValue);
                  return (
                <DropdownSelector 
                  id="version-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={normalizeVersionValue((data.params && data.params.version) || '')}
                  options={versionOptions.map(v => ({ value: v, label: String(v).replace(/^--\s*/, '') }))}
                  onChange={(val) => handleUpdate({ params: { ...(data.params||{}), version: val ? normalizeVersionValue(val) : undefined } })}
                  placeholder="版本"
                  title="MJ 版本 (--v)"
                />
                  );
                })()
              )}

              {dynamicImageParams.map((param) => {
                // ── 布尔类型参数：渲染成下拉框（开启/关闭）──
                if (param.type === 'boolean') {
                  const currentValue = data.params?.[param.key] ?? param.default ?? false;
                  return (
                    <DropdownSelector
                      key={`dynamic-image-param-${param.key}`}
                      id={`${param.key}-selector`}
                      activeId={activeDropdown}
                      setActiveId={setActiveDropdown}
                      value={currentValue}
                      options={[
                        { value: true, label: '开启' },
                        { value: false, label: '关闭' }
                      ]}
                      onChange={(val) => handleUpdate({ params: { ...(data.params||{}), [param.key]: val } })}
                      placeholder={getParamLabel(param.key)}
                      title={getParamLabel(param.key)}
                    />
                  );
                }

                // ── lora 类型参数：渲染成下拉框，支持 # 触发列表 ──
                if (param.type === 'lora') {
                  const currentValue = data.params?.[param.key] ?? param.default ?? null;
                  const loraOptions = [
                    { value: null, label: '关闭' },
                    ...(param.loras || []).map(lora => ({
                      value: lora.name,
                      label: lora.label || lora.name
                    }))
                  ];
                  return (
                    <DropdownSelector
                      key={`dynamic-image-param-${param.key}`}
                      id={`${param.key}-selector`}
                      activeId={activeDropdown}
                      setActiveId={setActiveDropdown}
                      value={currentValue}
                      options={loraOptions}
                      onChange={(val) => handleUpdate({ params: { ...(data.params||{}), [param.key]: val } })}
                      placeholder={getParamLabel(param.key)}
                      title={getParamLabel(param.key)}
                    />
                  );
                }

                // ── number 类型参数：数字输入框 ──
                if (param.type === 'number') {
                  const currentValue = data.params?.[param.key] ?? param.default ?? 0;
                  return (
                    <div key={`dynamic-image-param-${param.key}`} style={{ marginBottom: '8px' }}>
                      <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: '#666' }}>
                        {getParamLabel(param.key)}
                      </label>
                      <input
                        type="number"
                        min={param.min}
                        max={param.max}
                        value={currentValue}
                        onChange={(e) => {
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val)) {
                            handleUpdate({ params: { ...(data.params||{}), [param.key]: val } });
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '13px'
                        }}
                      />
                    </div>
                  );
                }

                // ── select 类型参数 ──
                // 参数联动过滤
                let filteredValues = param.values;
                if (paramDependsOn && paramDependsOn[param.key]) {
                  const dep = paramDependsOn[param.key];
                  const depValue = data.params?.[dep.field] || '';
                  if (depValue && dep.validOptions && dep.validOptions[depValue]) {
                    filteredValues = param.values.filter(v => dep.validOptions[depValue].includes(v));
                  }
                }
                return (
                <DropdownSelector
                  key={`dynamic-image-param-${param.key}`}
                  id={`dynamic-image-param-${param.key}`}
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={data.params && Object.prototype.hasOwnProperty.call(data.params, param.key) ? data.params[param.key] : ''}
                  options={filteredValues.map((value) => ({ value, label: getParamOptionLabel(param.key, value) }))}
                  onChange={(val) => {
                    const update = { params: { ...(data.params||{}), [param.key]: val === '' ? undefined : val } };
                    // size 参数修改时根据当前尺寸调整节点大小
                    if (param.key === 'size' && !data.output && val) {
                      const sz = ratioToPreviewSizeWithCurrent(val, data.customSize, data.storyboardMode);
                      if (sz) { update.customSize = sz; update._ratioPreview = true; }
                    }
                    // ── 联动重置：父字段变化时，若子字段当前值不在新可选范围内则自动重置为首项 ──
                    if (paramDependsOn) {
                      for (const [depKey, dep] of Object.entries(paramDependsOn)) {
                        if (dep.field === param.key && dep.validOptions && val) {
                          const validForNew = dep.validOptions[val] || [];
                          const currentDepVal = data.params?.[depKey];
                          if (currentDepVal && !validForNew.includes(currentDepVal)) {
                            update.params[depKey] = validForNew[0] || undefined;
                          }
                        }
                      }
                    }
                    handleUpdate(update);
                  }}
                  placeholder={getParamLabel(param.key)}
                  title={getParamLabel(param.key)}
                />
                );
              })}
            </>
          )}

          {/* 🆕 视频节点参数：比例、时长、清晰度 */}
          {data.type === 'video' && (
            <>
              {/* 多模态模型生成模式选择器（Seedance、HappyHorse 等，根据图片/音频/视频数量智能预选 + 过滤可选项） */}
              {configImageModes && configImageModes.length > 0 && supportsImageMode && (() => {
                // 可选项由 computeImageModeOptions 统一计算（与上方重算 effect 同源，
                // 入参也必须同源，否则两边算出的可选集会不一致）
                const { availableModes } = computeImageModeOptions({
                  imgCount: imageFiles.length,
                  hasAudio: audioFiles.length > 0,
                  hasVideo: videoFiles.length > 0,
                  isHappyHorse: isHappyHorseModel,
                  configImageModes,
                });
                if (availableModes.length === 0) return null;

                // 注：模式合法性纠正与素材变化重算统一由上方 useEffect 负责，
                //     这里只负责渲染，不再在 render 期间写节点数据。

                // 模式标签（支持 Seedance / MiniMax-H3 / HappyHorse）
                const modeLabels = {
                  'first_frame': '单图',
                  'first_last_frame': '首尾帧',
                  'multi_reference': '多模态',
                  'image-to-video': '单图',
                  'reference-to-video': '多模态',
                  'video-edit': '视频编辑'
                };

                return (
                  <DropdownSelector
                    id="image-mode-selector"
                    activeId={activeDropdown}
                    setActiveId={setActiveDropdown}
                    value={(data.params && data.params.image_mode) || ''}
                    options={availableModes.map(m => ({ value: m, label: modeLabels[m] || m }))}
                    onChange={(val) => handleUpdate({ params: { ...(data.params||{}), image_mode: val || undefined } })}
                    placeholder="生成模式"
                    title="生成模式"
                  />
                );
              })()}

              {configVideoRatios && configVideoRatios.length > 0 && (
                <DropdownSelector 
                  id="video-ratio-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && (data.params.aspect_ratio || data.params.ratio)) || ''}
                  options={configVideoRatios.map(r => ({ value: r, label: getParamOptionLabel('aspect_ratio', r) }))}
                  onChange={(val) => {
                    // 使用模型配置的参数名（aspect_ratio 或 ratio）
                    const paramKey = parsedEnabledParams.ratio ? 'ratio' : 'aspect_ratio';
                    const update = { params: { ...(data.params||{}), [paramKey]: val || undefined } };
                    // 视频比例修改时根据当前尺寸调整节点大小
                    if (!data.output) {
                      const sz = ratioToPreviewSizeWithCurrent(val, data.customSize, data.storyboardMode);
                      if (sz) { update.customSize = sz; update._ratioPreview = true; }
                    }
                    handleUpdate(update);
                  }}
                  placeholder="比例"
                  title="视频比例"
                />
              )}
              
              {configDurations && configDurations.length > 0 && (
                configDurations.length > 6 ? (
                  // 选项多（如 -1, 4-15 秒）→ 点击弹出垂直滑条 + 右侧"自动"胶囊按钮

                  (() => {

                    const hasAuto = configDurations.includes('-1') || configDurations.includes('auto');

                    const numericDurations = configDurations.filter(d => d !== '-1' && d !== 'auto');

                    if (numericDurations.length === 0) return null;

                    const sorted = [...numericDurations].map(Number).sort((a, b) => a - b);

                    const min = sorted[0];

                    const max = sorted[sorted.length - 1];


                    // 当前值：-1 或 auto 显示"自动"，否则显示数值

                    const rawDuration = data.params?.duration;

                    const isAuto = rawDuration === '-1' || rawDuration === 'auto';

                    const currentVal = isAuto ? -1 : Math.max(min, Math.min(max, Number(rawDuration) || min));


                    const sliderId = 'video-duration-slider';

                    const isSliderOpen = activeDropdown === sliderId;

                    const displayVal = isAuto ? '自动' : String(currentVal).padStart(2, ' ') + '秒';


                    return (

                      <div

                        className="nodrag"

                        style={{ position: 'relative', display: 'inline-block', flexShrink: 0, minWidth: 'fit-content' }}

                        onMouseDown={(e) => e.stopPropagation()}

                      >

                        <QuickTooltip content={isSliderOpen ? null : '视频时长'} delay={300}>

                          <div

                            onClick={(e) => { e.stopPropagation(); setActiveDropdown(isSliderOpen ? null : sliderId); }}

                            style={{

                              display: 'flex', alignItems: 'center', gap: 4,

                              padding: '6px 8px', borderRadius: 6, cursor: 'pointer',

                              fontSize: 13, color: isAuto ? '#fbbf24' : 'var(--text-body)', userSelect: 'none',

                              background: isSliderOpen ? 'var(--bg-hover)' : 'transparent',

                              transition: 'background 0.2s, color 0.2s'

                            }}

                            onMouseEnter={e => !isSliderOpen && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}

                            onMouseLeave={e => !isSliderOpen && (e.currentTarget.style.background = 'transparent')}

                          >

                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{displayVal}</span>

                            <ChevronDown size={14} style={{ opacity: 0.6, flexShrink: 0, transform: isSliderOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />

                          </div>

                        </QuickTooltip>

                        {isSliderOpen && (

                          <div

                            className="nowheel nodrag"

                            onClick={(e) => e.stopPropagation()}

                            onMouseDown={(e) => e.stopPropagation()}

                            onPointerDown={(e) => e.stopPropagation()}

                            style={{

                              position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',

                              marginTop: 4, background: 'var(--toolbar-bg)', border: '1px solid rgba(255,255,255,0.12)',

                              borderRadius: 8, padding: '10px 12px', zIndex: 1002,

                              boxShadow: '0 8px 24px rgba(0,0,0,0.5)',

                              display: 'flex', alignItems: 'center', gap: 8,

                              whiteSpace: 'nowrap'

                            }}

                          >

                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{min}s</span>

                            <input

                              type="range"

                              min={min} max={max} step={1} value={isAuto ? min : currentVal}

                              onChange={(e) => handleUpdate({ params: { ...(data.params||{}), duration: String(e.target.value) } })}

                              onMouseDown={(e) => e.stopPropagation()}

                              onPointerDown={(e) => e.stopPropagation()}

                              style={{ width: '120px', height: '4px', accentColor: '#3b82f6', cursor: 'pointer' }}

                            />

                            <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{max}s</span>

                            {hasAuto && (

                              <div

                                onClick={() => {
                                  const autoValue = configDurations.includes('auto') ? 'auto' : '-1';
                                  handleUpdate({ params: { ...(data.params||{}), duration: isAuto ? String(min) : autoValue } });
                                }}

                                style={{

                                  padding: '2px 6px', borderRadius: 4, cursor: 'pointer',

                                  fontSize: 11, fontWeight: 500, userSelect: 'none',

                                  background: isAuto ? '#fbbf24' : 'rgba(251,191,36,0.2)',

                                  color: isAuto ? '#000' : '#fbbf24',

                                  border: isAuto ? 'none' : '1px solid rgba(251,191,36,0.4)',

                                  transition: 'all 0.2s',

                                  marginLeft: 4

                                }}

                                onMouseEnter={(e) => {

                                  if (!isAuto) {

                                    e.currentTarget.style.background = 'rgba(251,191,36,0.3)';

                                  }

                                }}

                                onMouseLeave={(e) => {

                                  if (!isAuto) {

                                    e.currentTarget.style.background = 'rgba(251,191,36,0.2)';

                                  }

                                }}

                              >

                                自动

                              </div>

                            )}

                          </div>

                        )}

                      </div>

                    );

                  })()

                  ) : (
                  <DropdownSelector
                    id="video-duration-selector"
                    activeId={activeDropdown}
                    setActiveId={setActiveDropdown}
                    value={(data.params && data.params.duration) || ''}
                    options={configDurations.map(d => ({ value: d, label: d === '-1' ? '自动' : `${d}秒` }))}
                    onChange={(val) => handleUpdate({ params: { ...(data.params||{}), duration: val || undefined } })}
                    placeholder="时长"
                    title="视频时长"
                  />
                )
              )}
              
              {configVideoSizes && configVideoSizes.length > 0 && (
                <DropdownSelector
                  id="video-size-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && data.params.size) || ''}
                  options={configVideoSizes.map(s => ({ value: s, label: s === 'small' ? '720p（固定）' : s === 'large' ? '高清' : s === 'adaptive' ? '原尺寸' : s }))}
                  onChange={(val) => {
                    const update = { params: { ...(data.params||{}), size: val || undefined } };
                    // size 参数修改时根据当前尺寸调整节点大小
                    if (!data.output) {
                      const sz = ratioToPreviewSizeWithCurrent(val, data.customSize, data.storyboardMode);
                      if (sz) { update.customSize = sz; update._ratioPreview = true; }
                    }
                    handleUpdate(update);
                  }}
                  placeholder="清晰度"
                  title="视频清晰度"
                />
              )}

              {configResolutions && configResolutions.length > 0 && (
                <DropdownSelector
                  id="video-resolution-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && data.params.resolution) || ''}
                  options={configResolutions.map(r => ({ value: r, label: r }))}
                  onChange={(val) => handleUpdate({ params: { ...(data.params||{}), resolution: val || undefined } })}
                  placeholder="分辨率"
                  title="分辨率"
                />
              )}

              {/* 生成音频下拉（模型在 enabled_parameters 里声明了 generate_audio 才出现，影响价格倍率）
                  ⚠️ 不要再加模型名白名单：generate_audio 在 videoBuiltInParamKeys 里，
                     所以它不会掉进 dynamicVideoParams 兜底渲染 —— 这里挡掉就等于参数彻底消失。
                     （可灵/Vidu/万相这类同样有音频开关的模型名字里没有 seedance）
                  2.5 固定有声不给选：上面已把 configGenerateAudio 置空，这里再挡一层 */}
              {configGenerateAudio && configGenerateAudio.length > 0 && !isSeedance25Model && (() => {
                const raw = data.params?.generate_audio;
                // 旧节点可能存的是 boolean,显示时归一化成字符串以匹配下拉
                const value = raw === true ? 'true'
                            : raw === false ? 'false'
                            : (raw || '');

                return (
                  <DropdownSelector
                    id="seedance-generate-audio-selector"
                    activeId={activeDropdown}
                    setActiveId={setActiveDropdown}
                    value={value}
                    options={configGenerateAudio.map(v => ({
                      value: v,
                      label: v === 'true' ? '有声' : v === 'false' ? '无声' : v
                    }))}
                    onChange={(val) => handleUpdate({ params: { ...(data.params||{}), generate_audio: val || undefined } })}
                    placeholder="音频"
                    title="生成音频"
                  />
                );
              })()}

              {/* 🆕 运镜模板选择器 */}
              {configTemplates && configTemplates.length > 0 && (
                <DropdownSelector 
                  id="template-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && data.params.template_id) || ''}
                  options={configTemplates.map(t => ({ 
                    value: t, 
                    label: {
                      'hitchcock_dolly_in': '希区柯克推进',
                      'hitchcock_dolly_out': '希区柯克拉远',
                      'robo_arm': '机械臂',
                      'dynamic_orbit': '动感环绕',
                      'central_orbit': '中心环绕',
                      'crane_push': '起重机',
                      'quick_pull_back': '超级拉远',
                      'counterclockwise_swivel': '逆时针回旋',
                      'clockwise_swivel': '顺时针回旋',
                      'handheld': '手持运镜',
                      'rapid_push_pull': '快速推拉'
                    }[t] || t 
                  }))}
                  onChange={(val) => handleUpdate({ params: { ...(data.params||{}), template_id: val || undefined } })}
                  placeholder="运镜"
                  title="运镜模板"
                />
              )}
              
              {/* 🆕 运镜强度选择器 */}
              {configCameraStrength && configCameraStrength.length > 0 && (
                <DropdownSelector 
                  id="camera-strength-selector"
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={(data.params && data.params.camera_strength) || ''}
                  options={configCameraStrength.map(s => ({ 
                    value: s, 
                    label: { 'weak': '弱', 'medium': '中', 'strong': '强' }[s] || s 
                  }))}
                  onChange={(val) => handleUpdate({ params: { ...(data.params||{}), camera_strength: val || undefined } })}
                  placeholder="强度"
                  title="运镜强度"
                />
              )}

              {/* 🎬 运镜指令面板��MiniMax Hailuo 等支持运镜的模型） */}
              {hasCameraMovement && (
                <CameraMovementPanel
                  prompt={data.prompt || ''}
                  onInsert={(newPrompt) => { handleUpdate({ prompt: newPrompt }); setLocalPrompt(newPrompt); }}
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                />
              )}

              {dynamicVideoParams.map((param) => {
                // ── 布尔类型参数：渲染成下拉框（开启/关闭）──
                if (param.type === 'boolean') {
                  const currentValue = data.params?.[param.key] ?? param.default ?? false;
                  return (
                    <DropdownSelector
                      key={`dynamic-video-param-${param.key}`}
                      id={`${param.key}-selector`}
                      activeId={activeDropdown}
                      setActiveId={setActiveDropdown}
                      value={currentValue}
                      options={[
                        { value: true, label: '开启' },
                        { value: false, label: '关闭' }
                      ]}
                      onChange={(val) => handleUpdate({ params: { ...(data.params||{}), [param.key]: val } })}
                      placeholder={getParamLabel(param.key)}
                      title={getParamLabel(param.key)}
                    />
                  );
                }

                // ── select 类型参数 ──
                return (
                <DropdownSelector
                  key={`dynamic-video-param-${param.key}`}
                  id={`dynamic-video-param-${param.key}`}
                  activeId={activeDropdown}
                  setActiveId={setActiveDropdown}
                  value={data.params && Object.prototype.hasOwnProperty.call(data.params, param.key) ? data.params[param.key] : ''}
                  options={param.values.map((value) => ({ value, label: getParamOptionLabel(param.key, value) }))}
                  onChange={(val) => handleUpdate({ params: { ...(data.params||{}), [param.key]: val === '' ? undefined : val } })}
                  placeholder={getParamLabel(param.key)}
                  title={getParamLabel(param.key)}
                />
                );
              })}
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {/* 🎨 预设工具箱：同步显示该节点所属工具集 + 选中预设；仅当节点由预设生成（presetId 有效）时出现 */}
          {isImageGenNode && currentToolset && (
            <PresetDropdown
              id="preset-selector"
              activeId={activeDropdown}
              setActiveId={setActiveDropdown}
              toolset={currentToolset}
              value={data.presetId != null ? data.presetId : ''}
              disabled={presetDisabled}
              onChange={(val) => handleUpdate({ presetId: val })}
              onToggleDisabled={(next) => handleUpdate({ presetDisabled: next })}
            />
          )}
          {/* ✨ 技能工具箱：同步显示该节点所属工具集 + 选中技能；仅当节点由技能生成（skillboxPresetId 有效）时出现 */}
          {isImageGenNode && currentSkillboxToolset && (
            <SkillboxDropdown
              id="skillbox-selector"
              activeId={activeDropdown}
              setActiveId={setActiveDropdown}
              toolset={currentSkillboxToolset}
              value={data.skillboxPresetId != null ? data.skillboxPresetId : ''}
              disabled={skillboxDisabled}
              onChange={(val) => handleUpdate({ skillboxPresetId: val })}
              onToggleDisabled={(next) => handleUpdate({ skillboxDisabled: next })}
            />
          )}

          {/* 🆕 参考图按钮：仅当前技能启用参考图时显示 */}
          {isImageGenNode && refImageAssets.length > 0 && (
            <>
              <button
                ref={refImageButtonRef}
                data-refimage-button
                className="nodrag"
                onClick={() => setRefImageListVisible(!refImageListVisible)}
                style={{
                  padding: '6px 10px',
                  fontSize: '11px',
                  background: refImageListVisible ? 'var(--bg-hover)' : 'transparent',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => {
                  if (!refImageListVisible) e.currentTarget.style.background = 'var(--bg-hover)';
                }}
                onMouseLeave={(e) => {
                  if (!refImageListVisible) e.currentTarget.style.background = 'transparent';
                }}
              >
                {refImageLabel}
              </button>

              {/* 参考图列表弹窗 */}
              {refImageListVisible && (
                <div
                  data-refimage-list-popup
                  style={{
                    position: 'absolute',
                    top: refImageButtonRef.current ? `${refImageButtonRef.current.offsetTop + refImageButtonRef.current.offsetHeight + 4}px` : '100%',
                    left: 0,
                    right: 0,
                    zIndex: 9999
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MentionDropdown
                    query=""
                    assets={refImageAssets}
                    title={refImageLabel}
                    filterAsset={(asset) => asset?.type === 'refImage'}
                    onSelect={(asset) => {
                      handleRefImageSelect(asset);
                      setRefImageListVisible(false);
                    }}
                    onClose={() => setRefImageListVisible(false)}
                  />
                </div>
              )}
            </>
          )}

          {/* 🎨 画面风格：仅分镜大师节点显示，选中后生图时把对应风格词附加在最前层 */}
          {data.storyboardMode && (
            <StyleDropdown
              id="style-selector"
              activeId={activeDropdown}
              setActiveId={setActiveDropdown}
              value={data.storyboardStyleId != null ? data.storyboardStyleId : ''}
              onChange={(val) => handleUpdate({ storyboardStyleId: val })}
            />
          )}
        </div>
        {/* 右侧：生成次数 + 积分 + 发送按钮 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 8 }}>
          {isMjBatchLockedModel(currentModel) ? (
            <QuickTooltip content="该模型每次固定生成 4 张" position="top" delay={200}>
              <div style={{
                padding: '6px 8px', borderRadius: 6, fontSize: 13,
                color: '#64748b', cursor: 'default', userSelect: 'none'
              }}>1 X</div>
            </QuickTooltip>
          ) : (
            <DropdownSelector 
              id="batch-selector"
              activeId={activeDropdown}
              setActiveId={setActiveDropdown}
              value={data.batchCount || 1}
              options={[
                { value: 1, label: '1 X' },
                { value: 2, label: '2 X' },
                { value: 3, label: '3 X' },
                { value: 4, label: '4 X' }
              ]}
              onChange={(val) => handleUpdate({ batchCount: val })}
              placeholder="1 X"
              title="生成次数"
            />
          )}
          {/* 💰 积分价格实时显示
              公式：基础积分(credit_cost) × 参数倍率(时长/分辨率等) × 批次数(batchCount)
              倍率来源：enabled_parameters._multipliers，由管理员在 ApiConfigModal 配置
              无倍率配置时 totalMultiplier=1，行为与旧版一致 */}
          {currentModel && currentModel.credit_cost > 0 && (() => {
            const baseCost = currentModel.credit_cost;
            let totalMultiplier = 1;
            // 从 enabled_parameters 中提取 _multipliers
            let mults = {};
            try {
              const ep = typeof currentModel.enabled_parameters === 'string' 
                ? JSON.parse(currentModel.enabled_parameters) 
                : (currentModel.enabled_parameters || {});
              mults = ep._multipliers || {};
            } catch (e) {}
            // 遍历当前选中的参数值，累乘倍率
            const params = data.params || {};
            for (const [paramKey, paramMults] of Object.entries(mults)) {
              const selectedVal = params[paramKey];
              if (selectedVal && paramMults[selectedVal]) {
                totalMultiplier *= paramMults[selectedVal];
              }
            }
            const finalCost = Math.round(baseCost * totalMultiplier * (data.batchCount || 1));
            return (
              <QuickTooltip content="查看积分明细" position="top" delay={200}>
                <div 
                  onClick={() => window.dispatchEvent(new CustomEvent('open-credit-logs'))}
                  style={{ 
                    fontSize: 12, color: '#fbbf24', background: 'rgba(251,191,36,0.1)', 
                    padding: '4px 10px', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 4,
                    cursor: 'pointer', transition: 'background 0.15s'
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(251,191,36,0.2)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(251,191,36,0.1)'}
                >
                  <Sparkle size={12} color="#fbbf24" strokeWidth={2.5} style={{ marginRight: 2 }} /> {finalCost}
                </div>
              </QuickTooltip>
            );
          })()}
          <QuickTooltip content={(data.storyboardMode && !(data.gridCells || []).some(c => c && c.prompt && c.prompt.trim())) ? '请先填写分镜内容' : '立即生成 (Enter)'} position="top" delay={0}>
            <div 
                className="nodrag" 
                style={{
                  ...styles.sendBtn,
                  ...((data.loading || (data.storyboardMode && !(data.gridCells || []).some(c => c && c.prompt && c.prompt.trim()))) ? {
                    background: '#6b7280',
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    boxShadow: 'none'
                  } : {})
                }}
                onClick={() => {
                    if (data.loading) return;
                    // 🎬 分镜节点：没有任何已填写的分镜格子时禁止生成，避免生成空白分镜
                    if (data.storyboardMode && !(data.gridCells || []).some(c => c && c.prompt && c.prompt.trim())) return;
                    // 🔒 生成前确保 modelName/Logo 存在，用于 LoadingOverlay 显示
                    if (!data.modelName) {
                         const nameToUse = currentModel ? currentModel.name : "测试模型";
                         const logoToUse = currentModel ? currentModel.logo_url : null;
                         handleUpdate({ modelName: nameToUse, modelLogo: logoToUse });
                    } else if (currentModel && (data.modelName !== currentModel.name || data.modelLogo !== currentModel.logo_url)) {
                         handleUpdate({ modelName: currentModel.name, modelLogo: currentModel.logo_url });
                    }
                    data.onGenerate?.(id);
                }}
            >{
              data.loading ? 
                <Loader2 size={18} color="#fff" className="animate-spin" /> :
                <SendHorizontal size={18} color="#fff" />
            }</div>
          </QuickTooltip>
        </div>
      </div>

      {/* 🆕 全屏 Prompt 编辑器 Modal */}
      <FullScreenPromptModal
        isOpen={isFullscreenPrompt}
        onClose={() => setIsFullscreenPrompt(false)}
        prompt={data.prompt || ''}
        slateValue={data.slateValue}
        assets={promptMentionAssets}
        onChange={(newPrompt, newSlateValue) => handleUpdate(
          newSlateValue !== undefined ? { prompt: newPrompt, slateValue: newSlateValue } : { prompt: newPrompt }
        )}
        toolbarProps={toolbarProps}
        textPolishEnabled={textPolishEnabled}
        onPolish={handlePolish}
        isPolishing={isPolishing}
      />

      {/* 📝 文本润色预览弹窗 */}
      <TextPolishPreviewModal
        isOpen={!!polishPreview}
        originalText={polishPreview?.original || ''}
        polishedText={polishPreview?.polished || ''}
        isStreaming={isPolishing}
        onConfirm={handlePolishConfirm}
        onCancel={handlePolishCancel}
        onRepolish={handlePolish}
      />
    </div>
  );
});